import { apiClient } from '@/lib/api-client';
import type {
  Lead,
  Contact,
  Account,
  Deal,
  Task,
  TaskWithRelations,
  TaskHistory,
  DealHistory,
  Activity,
  Note,
  Pipeline,
  PipelineStage,
  PaginatedResponse,
  QueryParams,
  LeadStats,
  LeadMonitorStats,
  DealStats,
  TaskStats,
  ConvertLeadOptions,
  DuplicateResult,
  CheckDuplicateInput,
  MergeRecordsInput,
  LeadScoringRule,
  WebhookEndpoint,
  WebhookDelivery,
  AccountContact,
  AccountContactRole,
  ApprovalRequest,
  ApprovalWorkflow,
  Invoice,
  CustomFieldDefinition,
  SiteVisit,
  SiteVisitPhoto,
  SiteVisitUnit,
  UpsertSiteVisitUnitInput,
  SiteVisitStats,
  CreateSiteVisitInput,
  UpdateSiteVisitInput,
  CheckInInput,
  CheckOutInput,
  CompleteSiteVisitInput,
  PresignPhotoInput,
  PresignedPhotoResponse,
  ConfirmPhotoInput,
  Payment,
  CreditNote,
  PaymentTerm,
  PaymentMethod,
  TaxRegime,
  TaxComponent,
  TaxGroup,
  PriceBook,
  PriceBookEntry,
  DiscountRule,
  ExchangeRate,
  DocumentFlowItem,
  EntityHistory,
} from '../types/crm.common';
import type { ContactStats } from '@/types/crm';

// Helper to build query string
const buildQueryString = (params: QueryParams): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      query.append(key, String(value));
    }
  });
  return query.toString();
};

// CRM API Base URL (assumes gateway routes /crm to CRM service)
const CRM_BASE = '/crm';

// ============================================================================
// LEADS API
// ============================================================================
export const leadsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Lead>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Lead>>(`${CRM_BASE}/leads?${query}`);
    return response;
  },

  getById: async (id: string): Promise<Lead> => {
    const response = await apiClient.get<Lead>(`${CRM_BASE}/leads/${id}`);
    return response;
  },

  getStats: async (scope?: string): Promise<LeadStats> => {
    const params = scope ? `?scope=${scope}` : '';
    const response = await apiClient.get<LeadStats>(`${CRM_BASE}/leads/stats${params}`);
    return response;
  },

  getMonitorStats: async (scope?: string): Promise<LeadMonitorStats> => {
    const params = scope ? `?scope=${scope}` : '';
    const response = await apiClient.get<LeadMonitorStats>(
      `${CRM_BASE}/leads/stats/monitor${params}`,
    );
    return response;
  },

  create: async (data: Partial<Lead>): Promise<Lead> => {
    const response = await apiClient.post<Lead>(`${CRM_BASE}/leads`, data);
    return response;
  },

  update: async (id: string, data: Partial<Lead>): Promise<Lead> => {
    const response = await apiClient.put<Lead>(`${CRM_BASE}/leads/${id}`, data);
    return response;
  },

  updateStatus: async (id: string, status: string): Promise<Lead> => {
    const response = await apiClient.put<Lead>(`${CRM_BASE}/leads/${id}`, { status });
    return response;
  },

  /**
   * Cycle 45.7 — caller MUST pass an Idempotency-Key (UUID) to dedupe
   * double-clicks and mobile-network retries. Backend caches the first
   * successful response for 24h. Without the key, the request goes through
   * the legacy un-deduplicated path (still safe but allows duplicate
   * Account/Contact/Deal creation under tight races).
   */
  convert: async (id: string, data: ConvertLeadOptions, idempotencyKey?: string): Promise<Lead> => {
    const response = await apiClient.post<Lead>(
      `${CRM_BASE}/leads/${id}/convert`,
      data,
      idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {},
    );
    return response;
  },

  reassignOwner: async (id: string, ownerId: string): Promise<Lead> => {
    const response = await apiClient.patch<Lead>(`${CRM_BASE}/leads/${id}/owner`, { ownerId });
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/leads/${id}`);
  },

  // Bulk operations
  bulkUpdateStatus: async (ids: string[], status: string): Promise<{ updated: number }> => {
    const response = await apiClient.post<{ updated: number }>(
      `${CRM_BASE}/leads/bulk/update-status`,
      { ids, status },
    );
    return response;
  },

  bulkUpdateOwner: async (ids: string[], ownerId: string): Promise<{ updated: number }> => {
    const response = await apiClient.post<{ updated: number }>(
      `${CRM_BASE}/leads/bulk/update-owner`,
      { ids, ownerId },
    );
    return response;
  },

  bulkDelete: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(`${CRM_BASE}/leads/bulk/delete`, {
      ids,
    });
    return response;
  },

  exportCsv: async (ids?: string[]): Promise<Blob> => {
    const response = await apiClient.post<Blob>(
      `${CRM_BASE}/leads/export/csv`,
      { ids },
      { responseType: 'blob' },
    );
    return response;
  },
};

// ============================================================================
// CONTACTS API
// ============================================================================
export const contactsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Contact>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Contact>>(
      `${CRM_BASE}/contacts?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Contact> => {
    const response = await apiClient.get<Contact>(`${CRM_BASE}/contacts/${id}`);
    return response;
  },

  getStats: async (scope?: string): Promise<ContactStats> => {
    const params = scope ? `?scope=${scope}` : '';
    const response = await apiClient.get<ContactStats>(`${CRM_BASE}/contacts/stats${params}`);
    return response;
  },

  getByAccount: async (accountId: string): Promise<Contact[]> => {
    const response = await apiClient.get<Contact[]>(`${CRM_BASE}/contacts/by-account/${accountId}`);
    return response;
  },

  create: async (data: Partial<Contact>): Promise<Contact> => {
    const response = await apiClient.post<Contact>(`${CRM_BASE}/contacts`, data);
    return response;
  },

  update: async (id: string, data: Partial<Contact>): Promise<Contact> => {
    const response = await apiClient.put<Contact>(`${CRM_BASE}/contacts/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/contacts/${id}`);
  },

  // Bulk operations
  bulkUpdateOwner: async (ids: string[], ownerId: string): Promise<{ updated: number }> => {
    const response = await apiClient.post<{ updated: number }>(
      `${CRM_BASE}/contacts/bulk/update-owner`,
      {
        ids,
        ownerId,
      },
    );
    return response;
  },
};

// ============================================================================
// ACCOUNTS API
// ============================================================================
export const accountsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Account>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Account>>(
      `${CRM_BASE}/accounts?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Account> => {
    const response = await apiClient.get<Account>(`${CRM_BASE}/accounts/${id}`);
    return response;
  },

  getByIdWithStats: async (
    id: string,
  ): Promise<Account & { contactCount: number; dealCount: number; totalDealValue: number }> => {
    const response = await apiClient.get<
      Account & { contactCount: number; dealCount: number; totalDealValue: number }
    >(`${CRM_BASE}/accounts/${id}/with-stats`);
    return response;
  },

  getByIdWithDetails: async (
    id: string,
  ): Promise<Account & { contacts: Contact[]; deals: Deal[] }> => {
    const response = await apiClient.get<Account & { contacts: Contact[]; deals: Deal[] }>(
      `${CRM_BASE}/accounts/${id}/details`,
    );
    return response;
  },

  getHierarchy: async (
    id: string,
  ): Promise<{
    account: Account;
    parent?: Account | null;
    children: Account[];
    childCount: number;
  }> => {
    const response = await apiClient.get<{
      account: Account;
      parent?: Account | null;
      children: Account[];
      childCount: number;
    }>(`${CRM_BASE}/accounts/${id}/hierarchy`);
    return response;
  },

  getChildren: async (id: string): Promise<Account[]> => {
    const response = await apiClient.get<Account[]>(`${CRM_BASE}/accounts/${id}/children`);
    return response;
  },

  create: async (data: Partial<Account>): Promise<Account> => {
    const response = await apiClient.post<Account>(`${CRM_BASE}/accounts`, data);
    return response;
  },

  update: async (id: string, data: Partial<Account>): Promise<Account> => {
    const response = await apiClient.put<Account>(`${CRM_BASE}/accounts/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/accounts/${id}`);
  },

  // Bulk operations
  bulkUpdateOwner: async (ids: string[], ownerId: string): Promise<{ updated: number }> => {
    const response = await apiClient.post<{ updated: number }>(
      `${CRM_BASE}/accounts/bulk/update-owner`,
      {
        ids,
        ownerId,
      },
    );
    return response;
  },
};

// ============================================================================
// DEALS API
// ============================================================================
export const dealsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Deal>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Deal>>(`${CRM_BASE}/deals?${query}`);
    return response;
  },

  getById: async (id: string): Promise<Deal> => {
    const response = await apiClient.get<Deal>(`${CRM_BASE}/deals/${id}`);
    return response;
  },

  getByPipeline: async (pipelineId: string): Promise<Deal[]> => {
    const response = await apiClient.get<Deal[]>(`${CRM_BASE}/deals/pipeline/${pipelineId}`);
    return response;
  },

  getStats: async (scope?: string): Promise<DealStats> => {
    const params = scope ? `?scope=${scope}` : '';
    const response = await apiClient.get<DealStats>(`${CRM_BASE}/deals/stats${params}`);
    return response;
  },

  create: async (data: Partial<Deal>): Promise<Deal> => {
    const response = await apiClient.post<Deal>(`${CRM_BASE}/deals`, data);
    return response;
  },

  update: async (id: string, data: Partial<Deal>): Promise<Deal> => {
    const response = await apiClient.put<Deal>(`${CRM_BASE}/deals/${id}`, data);
    return response;
  },

  move: async (id: string, stageId: string): Promise<Deal> => {
    const response = await apiClient.post<Deal>(`${CRM_BASE}/deals/${id}/move`, { stageId });
    return response;
  },

  updateStage: async (id: string, _stage: string, stageId?: string): Promise<Deal> => {
    // Uses POST /move endpoint with MoveDealDto
    const response = await apiClient.post<Deal>(`${CRM_BASE}/deals/${id}/move`, { stageId });
    return response;
  },

  won: async (id: string): Promise<Deal> => {
    // Uses POST /close endpoint with CloseDealDto
    const response = await apiClient.post<Deal>(`${CRM_BASE}/deals/${id}/close`, { status: 'WON' });
    return response;
  },

  lost: async (id: string, reason?: string): Promise<Deal> => {
    // Uses POST /close endpoint with CloseDealDto
    const response = await apiClient.post<Deal>(`${CRM_BASE}/deals/${id}/close`, {
      status: 'LOST',
      lossReason: reason,
    });
    return response;
  },

  reassignOwner: async (id: string, ownerId: string): Promise<Deal> => {
    const response = await apiClient.patch<Deal>(`${CRM_BASE}/deals/${id}/owner`, { ownerId });
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/deals/${id}`);
  },

  // Bulk operations
  bulkUpdateStage: async (
    ids: string[],
    stage: string,
    stageId?: string,
  ): Promise<{ count: number }> => {
    const response = await apiClient.patch<{ count: number }>(`${CRM_BASE}/deals/bulk/stage`, {
      ids,
      stage,
      stageId,
    });
    return response;
  },

  bulkUpdateOwner: async (ids: string[], ownerId: string): Promise<{ updated: number }> => {
    const response = await apiClient.post<{ updated: number }>(
      `${CRM_BASE}/deals/bulk/update-owner`,
      { ids, ownerId },
    );
    return response;
  },

  bulkDelete: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(`${CRM_BASE}/deals/bulk/delete`, {
      ids,
    });
    return response;
  },

  syncOwnerToAccount: async (id: string): Promise<Deal> => {
    const response = await apiClient.patch<Deal>(`${CRM_BASE}/deals/${id}/sync-owner`);
    return response;
  },

  getHistory: async (id: string): Promise<DealHistory[]> => {
    const response = await apiClient.get<DealHistory[]>(`${CRM_BASE}/deals/${id}/history`);
    return response;
  },

  exportCsv: async (ids?: string[]): Promise<Blob> => {
    const response = await apiClient.post<Blob>(
      `${CRM_BASE}/deals/export/csv`,
      { ids },
      { responseType: 'blob' },
    );
    return response;
  },
};

// ============================================================================
// DEAL PRODUCTS API
// ============================================================================

export interface DealProduct {
  id: string;
  dealId: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  totalPrice: number;
  product?: {
    id: string;
    name: string;
    code?: string | null;
    category?: string | null;
    unitPrice: number;
    currency: string;
    isActive: boolean;
    /** Inventory (RR-14): line/tracking context of the linked product. */
    businessLineId?: string | null;
    statusId?: string | null;
  };
}

export const dealProductsApi = {
  getAll: async (dealId: string): Promise<DealProduct[]> => {
    const response = await apiClient.get<DealProduct[]>(`${CRM_BASE}/deals/${dealId}/products`);
    return response;
  },

  add: async (
    dealId: string,
    data: { productId: string; quantity?: number; unitPrice?: number; discount?: number },
  ): Promise<DealProduct> => {
    const response = await apiClient.post<DealProduct>(
      `${CRM_BASE}/deals/${dealId}/products`,
      data,
    );
    return response;
  },

  update: async (
    dealId: string,
    productId: string,
    data: { quantity?: number; unitPrice?: number; discount?: number },
  ): Promise<DealProduct> => {
    const response = await apiClient.put<DealProduct>(
      `${CRM_BASE}/deals/${dealId}/products/${productId}`,
      data,
    );
    return response;
  },

  remove: async (dealId: string, productId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/deals/${dealId}/products/${productId}`);
  },
};

// ============================================================================
// TASKS API
// ============================================================================
export const tasksApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Task>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Task>>(`${CRM_BASE}/tasks?${query}`);
    return response;
  },

  getById: async (id: string): Promise<Task> => {
    const response = await apiClient.get<Task>(`${CRM_BASE}/tasks/${id}`);
    return response;
  },

  getDetails: async (id: string): Promise<TaskWithRelations> => {
    const response = await apiClient.get<TaskWithRelations>(`${CRM_BASE}/tasks/${id}/details`);
    return response;
  },

  getHistory: async (id: string): Promise<TaskHistory[]> => {
    const response = await apiClient.get<TaskHistory[]>(`${CRM_BASE}/tasks/${id}/history`);
    return response;
  },

  getMyTasks: async (limit?: number): Promise<Task[]> => {
    const query = limit ? `?limit=${limit}` : '';
    const response = await apiClient.get<Task[]>(`${CRM_BASE}/tasks/my-tasks${query}`);
    return response;
  },

  getByEntity: async (entityType: string, entityId: string): Promise<Task[]> => {
    const response = await apiClient.get<Task[]>(
      `${CRM_BASE}/tasks/by-entity/${entityType}/${entityId}`,
    );
    return response;
  },

  getStats: async (scope?: string): Promise<TaskStats> => {
    const query = scope ? `?scope=${scope}` : '';
    const response = await apiClient.get<TaskStats>(`${CRM_BASE}/tasks/stats${query}`);
    return response;
  },

  create: async (data: Partial<Task>): Promise<Task> => {
    const response = await apiClient.post<Task>(`${CRM_BASE}/tasks`, data);
    return response;
  },

  update: async (id: string, data: Partial<Task>): Promise<Task> => {
    const response = await apiClient.put<Task>(`${CRM_BASE}/tasks/${id}`, data);
    return response;
  },

  complete: async (
    id: string,
    body?: { outcome?: string; completionNotes?: string; forceComplete?: boolean },
  ): Promise<Task> => {
    const response = await apiClient.patch<Task>(`${CRM_BASE}/tasks/${id}/complete`, body ?? {});
    return response;
  },

  reopen: async (id: string): Promise<Task> => {
    const response = await apiClient.patch<Task>(`${CRM_BASE}/tasks/${id}/reopen`);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/tasks/${id}`);
  },

  bulkDelete: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(`${CRM_BASE}/tasks/bulk/delete`, {
      ids,
    });
    return response;
  },

  bulkComplete: async (
    ids: string[],
  ): Promise<{
    completed: number;
    blocked: Array<{ id: string; reason: string }>;
  }> => {
    const response = await apiClient.post<{
      completed: number;
      blocked: Array<{ id: string; reason: string }>;
    }>(`${CRM_BASE}/tasks/bulk/complete`, { ids });
    return response;
  },
};

// ============================================================================
// ACTIVITIES API
// ============================================================================
export const activitiesApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Activity>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Activity>>(
      `${CRM_BASE}/activities?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Activity> => {
    const response = await apiClient.get<Activity>(`${CRM_BASE}/activities/${id}`);
    return response;
  },

  getByEntity: async (entityType: string, entityId: string): Promise<Activity[]> => {
    const response = await apiClient.get<Activity[]>(
      `${CRM_BASE}/activities/by-entity/${entityType}/${entityId}`,
    );
    return response;
  },

  getStats: async (): Promise<{
    total: number;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
    thisWeek: number;
    thisMonth: number;
  }> => {
    const response = await apiClient.get<{
      total: number;
      byType: Record<string, number>;
      byStatus: Record<string, number>;
      thisWeek: number;
      thisMonth: number;
    }>(`${CRM_BASE}/activities/stats`);
    return response;
  },

  create: async (data: Partial<Activity>): Promise<Activity> => {
    const response = await apiClient.post<Activity>(`${CRM_BASE}/activities`, data);
    return response;
  },

  update: async (id: string, data: Partial<Activity>): Promise<Activity> => {
    const response = await apiClient.put<Activity>(`${CRM_BASE}/activities/${id}`, data);
    return response;
  },

  complete: async (id: string, outcome?: string): Promise<Activity> => {
    const response = await apiClient.patch<Activity>(`${CRM_BASE}/activities/${id}/complete`, {
      outcome,
    });
    return response;
  },

  cancel: async (id: string): Promise<Activity> => {
    const response = await apiClient.patch<Activity>(`${CRM_BASE}/activities/${id}/cancel`);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/activities/${id}`);
  },
};

// ============================================================================
// NOTES API
// ============================================================================
export const notesApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Note>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Note>>(`${CRM_BASE}/notes?${query}`);
    return response;
  },

  getById: async (id: string): Promise<Note> => {
    const response = await apiClient.get<Note>(`${CRM_BASE}/notes/${id}`);
    return response;
  },

  getByEntity: async (entityType: string, entityId: string): Promise<Note[]> => {
    const response = await apiClient.get<Note[]>(
      `${CRM_BASE}/notes/by-entity/${entityType}/${entityId}`,
    );
    return response;
  },

  create: async (data: Partial<Note>): Promise<Note> => {
    const response = await apiClient.post<Note>(`${CRM_BASE}/notes`, data);
    return response;
  },

  update: async (id: string, data: Partial<Note>): Promise<Note> => {
    const response = await apiClient.put<Note>(`${CRM_BASE}/notes/${id}`, data);
    return response;
  },

  pin: async (id: string): Promise<Note> => {
    const response = await apiClient.patch<Note>(`${CRM_BASE}/notes/${id}/pin`);
    return response;
  },

  unpin: async (id: string): Promise<Note> => {
    const response = await apiClient.patch<Note>(`${CRM_BASE}/notes/${id}/unpin`);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/notes/${id}`);
  },
};

// ============================================================================
// PIPELINES API
// ============================================================================
export const pipelinesApi = {
  getAll: async (): Promise<Pipeline[]> => {
    return apiClient.get<Pipeline[]>(`${CRM_BASE}/pipelines`);
  },

  getById: async (id: string): Promise<Pipeline> => {
    const response = await apiClient.get<Pipeline>(`${CRM_BASE}/pipelines/${id}`);
    return response;
  },

  getDefault: async (): Promise<Pipeline> => {
    const response = await apiClient.get<Pipeline>(`${CRM_BASE}/pipelines/default`);
    return response;
  },

  create: async (data: Partial<Pipeline>): Promise<Pipeline> => {
    const response = await apiClient.post<Pipeline>(`${CRM_BASE}/pipelines`, data);
    return response;
  },

  update: async (id: string, data: Partial<Pipeline>): Promise<Pipeline> => {
    const response = await apiClient.put<Pipeline>(`${CRM_BASE}/pipelines/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/pipelines/${id}`);
  },

  // Stage operations
  getStages: async (pipelineId: string): Promise<PipelineStage[]> => {
    const response = await apiClient.get<PipelineStage[]>(
      `${CRM_BASE}/pipelines/${pipelineId}/stages`,
    );
    return response;
  },

  addStage: async (pipelineId: string, data: Partial<PipelineStage>): Promise<PipelineStage> => {
    const response = await apiClient.post<PipelineStage>(
      `${CRM_BASE}/pipelines/${pipelineId}/stages`,
      data,
    );
    return response;
  },

  updateStage: async (
    pipelineId: string,
    stageId: string,
    data: Partial<PipelineStage>,
  ): Promise<PipelineStage> => {
    const response = await apiClient.put<PipelineStage>(
      `${CRM_BASE}/pipelines/${pipelineId}/stages/${stageId}`,
      data,
    );
    return response;
  },

  deleteStage: async (pipelineId: string, stageId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/pipelines/${pipelineId}/stages/${stageId}`);
  },

  // Initialization (for existing tenants without default pipeline)
  initialize: async (): Promise<{ message: string; created: boolean; pipelineId: string }> => {
    const response = await apiClient.post<{
      message: string;
      created: boolean;
      pipelineId: string;
    }>(`${CRM_BASE}/pipelines/initialize`);
    return response;
  },
};

// ============================================================================
// EMAIL TEMPLATES API
// ============================================================================
export interface EmailTemplate {
  id: string;
  tenantId: string;
  name: string;
  subject: string;
  body: string;
  category: string;
  status: string;
  description?: string | null;
  variables: string[];
  usageCount: number;
  lastUsedAt?: Date | null;
  createdBy?: string | null;
  createdByName?: string;
  createdAt: Date;
  updatedAt: Date;
}

export const emailTemplatesApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<EmailTemplate>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<EmailTemplate>>(
      `${CRM_BASE}/email-templates?${query}`,
    );
    return response;
  },

  // Load the built-in starter campaign templates into this tenant (idempotent —
  // skips any that already exist by name). Lets an existing tenant populate an
  // empty library without re-provisioning.
  seedDefaults: async (): Promise<{ seeded: number }> => {
    const response = await apiClient.post<{ seeded: number }>(
      `${CRM_BASE}/email-templates/seed-defaults`,
      {},
    );
    return response;
  },

  getById: async (id: string): Promise<EmailTemplate> => {
    const response = await apiClient.get<EmailTemplate>(`${CRM_BASE}/email-templates/${id}`);
    return response;
  },

  getByCategory: async (category: string): Promise<EmailTemplate[]> => {
    const response = await apiClient.get<EmailTemplate[]>(
      `${CRM_BASE}/email-templates/category/${category}`,
    );
    return response;
  },

  create: async (data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    const response = await apiClient.post<EmailTemplate>(`${CRM_BASE}/email-templates`, data);
    return response;
  },

  update: async (id: string, data: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    const response = await apiClient.put<EmailTemplate>(`${CRM_BASE}/email-templates/${id}`, data);
    return response;
  },

  duplicate: async (id: string): Promise<EmailTemplate> => {
    const response = await apiClient.post<EmailTemplate>(
      `${CRM_BASE}/email-templates/${id}/duplicate`,
    );
    return response;
  },

  archive: async (id: string): Promise<EmailTemplate> => {
    const response = await apiClient.patch<EmailTemplate>(
      `${CRM_BASE}/email-templates/${id}/archive`,
    );
    return response;
  },

  activate: async (id: string): Promise<EmailTemplate> => {
    const response = await apiClient.patch<EmailTemplate>(
      `${CRM_BASE}/email-templates/${id}/activate`,
    );
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/email-templates/${id}`);
  },

  preview: async (
    id: string,
    context: Record<string, unknown>,
  ): Promise<{ subject: string; body: string }> => {
    const response = await apiClient.post<{ subject: string; body: string }>(
      `${CRM_BASE}/email-templates/${id}/preview`,
      context,
    );
    return response;
  },

  send: async (
    id: string,
    data: { to: string; entityType?: string; entityId?: string },
  ): Promise<{ success: boolean; messageId?: string }> => {
    const response = await apiClient.post<{ success: boolean; messageId?: string }>(
      `${CRM_BASE}/email-templates/${id}/send`,
      data,
    );
    return response;
  },
};

// ============================================================================
// CAMPAIGNS API
// ============================================================================
export interface Campaign {
  id: string;
  tenantId: string;
  name: string;
  type:
    | 'EMAIL'
    | 'SOCIAL'
    | 'WEBINAR'
    | 'TRADE_SHOW'
    | 'ADVERTISEMENT'
    | 'REFERRAL_PROGRAM'
    | 'TELEMARKETING'
    | 'OTHER';
  status: 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'ABORTED';
  description?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  budget?: number | null;
  actualCost?: number | null;
  expectedRevenue?: number | null;
  expectedResponse?: number | null;
  targetAudience?: string | null;
  ownerId?: string | null;
  owner?: { id: string; firstName: string; lastName: string } | null;
  // Engagement metrics (backend campaigns.service mapToResponse). responseRate
  // and roi are only present when calculable (numberSent > 0 / actualCost > 0).
  numberSent: number;
  numberResponded: number;
  responseRate?: number;
  roi?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CampaignMember {
  id: string;
  campaignId: string;
  leadId?: string | null;
  contactId?: string | null;
  // Matches backend Prisma enum CampaignMemberStatus (01-enums.prisma). Members
  // progress PENDING → QUEUED → SENT → DELIVERED → OPENED → CLICKED via the Zepto
  // webhook. QUEUED = atomically claimed for send (crash-safe resumability).
  status:
    | 'PENDING'
    | 'QUEUED'
    | 'SENT'
    | 'DELIVERED'
    | 'OPENED'
    | 'CLICKED'
    | 'BOUNCED'
    | 'UNSUBSCRIBED'
    | 'SUPPRESSED'
    | 'FAILED';
  sentAt?: Date | null;
  deliveredAt?: Date | null;
  openedAt?: Date | null;
  clickedAt?: Date | null;
  notes?: string | null;
  createdAt: Date;
  updatedAt: Date;
  // Resolved recipient (name + email) so the UI shows people, not raw ids.
  member?: {
    type: 'lead' | 'contact';
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
  } | null;
}

/** Furthest-status bucket counts for a campaign's members (from getStats). */
export interface CampaignMemberStats {
  total: number;
  pending: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  failed: number;
  suppressed: number;
  openRate?: number;
  clickRate?: number;
  bounceRate?: number;
}

export const campaignsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Campaign>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Campaign>>(
      `${CRM_BASE}/campaigns?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Campaign> => {
    const response = await apiClient.get<Campaign>(`${CRM_BASE}/campaigns/${id}`);
    return response;
  },

  getStats: async (): Promise<{
    total: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    totalBudget: number;
    totalActualCost: number;
  }> => {
    const response = await apiClient.get<{
      total: number;
      byStatus: Record<string, number>;
      byType: Record<string, number>;
      totalBudget: number;
      totalActualCost: number;
    }>(`${CRM_BASE}/campaigns/stats`);
    return response;
  },

  // Verified sender domains + the recommended default From address. The email
  // form checks the From address against `verifiedDomains` so a user can't
  // launch a blast on an unverified domain (which the mail provider rejects
  // per-recipient, silently, with no bounce back to the campaign).
  getSenderInfo: async (): Promise<{
    verifiedDomains: string[];
    defaultFromEmail: string;
    defaultFromName: string;
  }> => {
    const response = await apiClient.get<{
      verifiedDomains: string[];
      defaultFromEmail: string;
      defaultFromName: string;
    }>(`${CRM_BASE}/campaigns/sender-info`);
    return response;
  },

  create: async (data: Partial<Campaign>): Promise<Campaign> => {
    const response = await apiClient.post<Campaign>(`${CRM_BASE}/campaigns`, data);
    return response;
  },

  // Duplicate an existing campaign (copies the campaign details, resets status
  // to PLANNED and clears sent/engagement counters; the email is composed
  // fresh) so a similar campaign can be spun up in one click instead of
  // re-filling the whole form.
  clone: async (id: string, name?: string): Promise<Campaign> => {
    const response = await apiClient.post<Campaign>(
      `${CRM_BASE}/campaigns/${id}/clone`,
      name ? { name } : {},
    );
    return response;
  },

  update: async (id: string, data: Partial<Campaign>): Promise<Campaign> => {
    const response = await apiClient.put<Campaign>(`${CRM_BASE}/campaigns/${id}`, data);
    return response;
  },

  updateStatus: async (id: string, status: Campaign['status']): Promise<Campaign> => {
    const response = await apiClient.patch<Campaign>(`${CRM_BASE}/campaigns/${id}/status`, {
      status,
    });
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/campaigns/${id}`);
  },

  // Campaign Members
  addMembers: async (
    id: string,
    data: { leadIds?: string[]; contactIds?: string[] },
  ): Promise<{ added: number; skipped: number }> => {
    const response = await apiClient.post<{ added: number; skipped: number }>(
      `${CRM_BASE}/campaigns/${id}/members`,
      data,
    );
    return response;
  },

  addMembersByFilter: async (
    id: string,
    filter: { status?: string; source?: string; rating?: string },
  ): Promise<{ added: number }> => {
    const response = await apiClient.post<{ added: number }>(
      `${CRM_BASE}/campaigns/${id}/members/filter`,
      filter,
    );
    return response;
  },

  getMembers: async (
    id: string,
    params: QueryParams = {},
  ): Promise<PaginatedResponse<CampaignMember>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<CampaignMember>>(
      `${CRM_BASE}/campaigns/${id}/members?${query}`,
    );
    return response;
  },

  getMemberStats: async (id: string): Promise<CampaignMemberStats> => {
    const response = await apiClient.get<CampaignMemberStats>(
      `${CRM_BASE}/campaigns/${id}/members/stats`,
    );
    return response;
  },

  getMember: async (campaignId: string, memberId: string): Promise<CampaignMember> => {
    const response = await apiClient.get<CampaignMember>(
      `${CRM_BASE}/campaigns/${campaignId}/members/${memberId}`,
    );
    return response;
  },

  removeMember: async (campaignId: string, memberId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/campaigns/${campaignId}/members/${memberId}`);
  },

  removeAllMembers: async (id: string): Promise<{ removed: number }> => {
    const response = await apiClient.delete<{ removed: number }>(
      `${CRM_BASE}/campaigns/${id}/members`,
    );
    return response;
  },

  // Campaign Email
  // NOTE: the backend CreateCampaignEmailDto uses `bodyHtml` (required) and the
  // ValidationPipe is `forbidNonWhitelisted` — sending `body` previously 400'd on
  // both "property body should not exist" and "bodyHtml must be a string".
  createEmail: async (
    id: string,
    data: {
      subject: string;
      bodyHtml: string;
      bodyText?: string;
      fromName?: string;
      fromEmail: string;
    },
  ): Promise<{ id: string; campaignId: string; subject: string; bodyHtml: string }> => {
    const response = await apiClient.post<{
      id: string;
      campaignId: string;
      subject: string;
      bodyHtml: string;
    }>(`${CRM_BASE}/campaigns/${id}/email`, data);
    return response;
  },

  getEmail: async (
    id: string,
  ): Promise<{ id: string; campaignId: string; subject: string; bodyHtml: string } | null> => {
    const response = await apiClient.get<{
      id: string;
      campaignId: string;
      subject: string;
      bodyHtml: string;
    } | null>(`${CRM_BASE}/campaigns/${id}/email`);
    return response;
  },

  updateEmail: async (
    id: string,
    data: {
      subject?: string;
      bodyHtml?: string;
      bodyText?: string;
      fromName?: string;
      fromEmail?: string;
    },
  ): Promise<{ id: string; campaignId: string; subject: string; bodyHtml: string }> => {
    const response = await apiClient.put<{
      id: string;
      campaignId: string;
      subject: string;
      bodyHtml: string;
    }>(`${CRM_BASE}/campaigns/${id}/email`, data);
    return response;
  },

  // Returns the signed public download link for a campaign asset (brochure /
  // pamphlet) to insert into the email body. The send path re-signs it per
  // recipient; this canonical link is what the composer drops in.
  getAssetLink: async (campaignId: string, attachmentId: string): Promise<{ url: string }> => {
    return apiClient.post<{ url: string }>(
      `${CRM_BASE}/campaigns/${campaignId}/assets/${attachmentId}/link`,
    );
  },

  deleteEmail: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/campaigns/${id}/email`);
  },

  previewEmail: async (
    id: string,
    data: { memberId?: string },
  ): Promise<{ subject: string; bodyHtml: string }> => {
    const response = await apiClient.post<{ subject: string; bodyHtml: string }>(
      `${CRM_BASE}/campaigns/${id}/email/preview`,
      data,
    );
    return response;
  },

  // Campaign Execution
  execute: async (
    id: string,
  ): Promise<{
    campaignId: string;
    totalMembers: number;
    sent: number;
    failed: number;
    skipped: number;
    suppressed: number;
  }> => {
    const response = await apiClient.post<{
      campaignId: string;
      totalMembers: number;
      sent: number;
      failed: number;
      skipped: number;
      suppressed: number;
    }>(`${CRM_BASE}/campaigns/${id}/execute`);
    return response;
  },

  getExecutionStatus: async (
    id: string,
  ): Promise<{
    status: 'idle' | 'running' | 'completed' | 'failed';
    progress: number;
    totalMembers: number;
    sent: number;
    failed: number;
    suppressed: number;
  } | null> => {
    const response = await apiClient.get<{
      status: 'idle' | 'running' | 'completed' | 'failed';
      progress: number;
      totalMembers: number;
      sent: number;
      failed: number;
      suppressed: number;
    } | null>(`${CRM_BASE}/campaigns/${id}/execution-status`);
    return response;
  },

  sendTestEmail: async (id: string, email: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `${CRM_BASE}/campaigns/${id}/send-test`,
      { email },
    );
    return response;
  },
};

// ============================================================================
// PRODUCTS API
// ============================================================================
export interface Product {
  id: string;
  tenantId: string;
  name: string;
  code?: string | null;
  description?: string | null;
  category?: string | null;
  unitPrice: number;
  currency: string;
  costPrice?: number | null;
  type: 'PRODUCT' | 'SERVICE' | 'SUBSCRIPTION';
  isRecurring: boolean;
  billingFrequency?: 'ONE_TIME' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | null;
  trackInventory: boolean;
  stockQuantity?: number | null;
  reorderLevel?: number | null;
  unitOfMeasure?: string | null;
  hsnCode?: string | null;
  defaultTaxGroupId?: string | null;
  customFields?: Record<string, unknown>;
  isActive: boolean;
  /** Inventory (v1.4): line/status context — present on tracked units. */
  businessLineId?: string | null;
  unitNo?: string | null;
  status?: { id: string; name: string; semantic: string; color?: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export const productsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Product>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Product>>(
      `${CRM_BASE}/products?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Product> => {
    const response = await apiClient.get<Product>(`${CRM_BASE}/products/${id}`);
    return response;
  },

  getCategories: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`${CRM_BASE}/products/categories`);
    return response;
  },

  create: async (data: Partial<Product>): Promise<Product> => {
    const response = await apiClient.post<Product>(`${CRM_BASE}/products`, data);
    return response;
  },

  update: async (id: string, data: Partial<Product>): Promise<Product> => {
    const response = await apiClient.put<Product>(`${CRM_BASE}/products/${id}`, data);
    return response;
  },

  activate: async (id: string): Promise<Product> => {
    const response = await apiClient.patch<Product>(`${CRM_BASE}/products/${id}/activate`);
    return response;
  },

  deactivate: async (id: string): Promise<Product> => {
    const response = await apiClient.patch<Product>(`${CRM_BASE}/products/${id}/deactivate`);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/products/${id}`);
  },
};

// ============================================================================
// VENDORS API
// ============================================================================
export type VendorType =
  | 'SUPPLIER'
  | 'DEALER'
  | 'DISTRIBUTOR'
  | 'MANUFACTURER'
  | 'SERVICE_PROVIDER';
export type VendorStatus =
  | 'PROSPECTIVE'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'ON_HOLD'
  | 'BLACKLISTED';

export interface Vendor {
  id: string;
  tenantId: string;
  name: string;
  code?: string | null;
  type: VendorType;
  category?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  status: VendorStatus;
  rating?: number | null;
  ownerId?: string | null;
  owner?: { id: string; firstName: string; lastName: string } | null;
  paymentTerms?: string | null;
  creditLimit?: number | null;
  panNumber?: string | null;
  gstNumber?: string | null;
  taxId?: string | null;
  purchasingBlocked: boolean;
  paymentBlocked: boolean;
  description?: string | null;
  tags: string[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  version?: number;
}

export const vendorsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Vendor>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Vendor>>(`${CRM_BASE}/vendors?${query}`);
    return response;
  },

  getById: async (id: string): Promise<Vendor> => {
    const response = await apiClient.get<Vendor>(`${CRM_BASE}/vendors/${id}`);
    return response;
  },

  getCategories: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`${CRM_BASE}/vendors/categories`);
    return response;
  },

  create: async (data: Record<string, unknown>): Promise<Vendor> => {
    const response = await apiClient.post<Vendor>(`${CRM_BASE}/vendors`, data);
    return response;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<Vendor> => {
    const response = await apiClient.put<Vendor>(`${CRM_BASE}/vendors/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/vendors/${id}`);
  },

  submitForApproval: async (id: string): Promise<Vendor> => {
    const response = await apiClient.post<Vendor>(`${CRM_BASE}/vendors/${id}/submit-for-approval`);
    return response;
  },

  // Vendor Contacts
  addContact: async (
    id: string,
    data: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      jobTitle?: string;
      isPrimary?: boolean;
    },
  ): Promise<Vendor> => {
    const response = await apiClient.post<Vendor>(`${CRM_BASE}/vendors/${id}/contacts`, data);
    return response;
  },

  updateContact: async (
    id: string,
    contactId: string,
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      jobTitle?: string;
      isPrimary?: boolean;
    },
  ): Promise<Vendor> => {
    const response = await apiClient.put<Vendor>(
      `${CRM_BASE}/vendors/${id}/contacts/${contactId}`,
      data,
    );
    return response;
  },

  deleteContact: async (id: string, contactId: string): Promise<Vendor> => {
    const response = await apiClient.delete<Vendor>(
      `${CRM_BASE}/vendors/${id}/contacts/${contactId}`,
    );
    return response;
  },
};

// ============================================================================
// QUOTES API
// ============================================================================
export interface Quote {
  id: string;
  tenantId: string;
  quoteNumber: string;
  name: string;
  dealId: string;
  accountId?: string | null;
  contactId?: string | null;
  status:
    | 'DRAFT'
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'SENT'
    | 'ACCEPTED'
    | 'REJECTED'
    | 'EXPIRED'
    | 'CANCELLED'
    | 'SUPERSEDED';
  quoteDate: Date;
  validUntil?: Date | null;
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  taxPercent: number;
  taxAmount: number;
  total: number;
  currency: string;
  exchangeRate?: number | null;
  baseCurrencyTotal?: number | null;
  terms?: string | null;
  notes?: string | null;
  needsApproval: boolean;
  approvedById?: string | null;
  approvedAt?: Date | null;
  sentAt?: Date | null;
  acceptedAt?: Date | null;
  rejectedAt?: Date | null;
  paymentTermId?: string | null;
  customerReference?: string | null;
  shippingAddress?: string | null;
  billingAddress?: string | null;
  incoterm?: string | null;
  incotermLocation?: string | null;
  ownerId?: string | null;
  createdById?: string | null;
  version: number;
  parentQuoteId?: string | null;
  createdAt: Date;
  updatedAt: Date;
  items?: QuoteItem[];
  dealName?: string;
  accountName?: string;
  contactName?: string;
  ownerName?: string;
  paymentTermName?: string;
}

export interface QuoteItem {
  id: string;
  quoteId: string;
  productId?: string | null;
  productName?: string;
  description: string;
  quantity: number;
  unitOfMeasure?: string | null;
  unitPrice: number;
  discount: number;
  totalPrice: number;
  sortOrder: number;
  taxGroupId?: string | null;
  taxRate: number;
  taxAmount: number;
  taxBreakdown?: Record<string, unknown>[] | null;
  hsnCode?: string | null;
  discountBreakdown?: Record<string, unknown>[] | null;
}

export const quotesApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Quote>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Quote>>(`${CRM_BASE}/quotes?${query}`);
    return response;
  },

  getById: async (id: string): Promise<Quote> => {
    const response = await apiClient.get<Quote>(`${CRM_BASE}/quotes/${id}`);
    return response;
  },

  getByIdWithDetails: async (id: string): Promise<Quote> => {
    const response = await apiClient.get<Quote>(`${CRM_BASE}/quotes/${id}/details`);
    return response;
  },

  create: async (data: Partial<Quote>): Promise<Quote> => {
    const response = await apiClient.post<Quote>(`${CRM_BASE}/quotes`, data);
    return response;
  },

  update: async (id: string, data: Partial<Quote>): Promise<Quote> => {
    const response = await apiClient.put<Quote>(`${CRM_BASE}/quotes/${id}`, data);
    return response;
  },

  updateStatus: async (
    id: string,
    status: Quote['status'],
    rejectionReason?: string,
  ): Promise<Quote> => {
    const body: { status: Quote['status']; rejectionReason?: string } = { status };
    if (rejectionReason) body.rejectionReason = rejectionReason;
    const response = await apiClient.patch<Quote>(`${CRM_BASE}/quotes/${id}/status`, body);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/quotes/${id}`);
  },

  // Quote Items
  addItem: async (
    id: string,
    data: {
      productId?: string;
      description: string;
      quantity: number;
      unitPrice?: number;
      discountPercent?: number;
      unitOfMeasure?: string;
    },
  ): Promise<Quote> => {
    const response = await apiClient.post<Quote>(`${CRM_BASE}/quotes/${id}/items`, data);
    return response;
  },

  updateItem: async (
    id: string,
    itemId: string,
    data: {
      description?: string;
      quantity?: number;
      unitPrice?: number;
      discountPercent?: number;
      unitOfMeasure?: string;
    },
  ): Promise<Quote> => {
    const response = await apiClient.put<Quote>(`${CRM_BASE}/quotes/${id}/items/${itemId}`, data);
    return response;
  },

  deleteItem: async (id: string, itemId: string): Promise<Quote> => {
    const response = await apiClient.delete<Quote>(`${CRM_BASE}/quotes/${id}/items/${itemId}`);
    return response;
  },

  downloadPdf: async (id: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(`${CRM_BASE}/quotes/${id}/pdf`, {
      responseType: 'blob',
    });
    return response;
  },

  createRevision: async (id: string): Promise<Quote> => {
    const response = await apiClient.post<Quote>(`${CRM_BASE}/quotes/${id}/revisions`);
    return response;
  },

  createFromDeal: async (dealId: string): Promise<Quote> => {
    const response = await apiClient.post<Quote>(`${CRM_BASE}/quotes/from-deal/${dealId}`);
    return response;
  },
};

// ============================================================================
// SALES ORDERS API
// ============================================================================
export interface SalesOrder {
  id: string;
  tenantId: string;
  orderNumber: string;
  quoteId?: string | null;
  dealId?: string | null;
  accountId?: string | null;
  contactId?: string | null;
  ownerId?: string | null;
  status:
    | 'DRAFT'
    | 'SUBMITTED'
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'REJECTED'
    | 'PARTIALLY_FULFILLED'
    | 'FULFILLED'
    | 'CANCELLED';
  orderDate: Date;
  // SAP-standard header fields
  name?: string | null;
  requestedDeliveryDate?: Date | null;
  validUntil?: Date | null;
  shippingCondition?: string | null;
  rejectionReason?: string | null;
  // Status timestamps
  submittedAt?: Date | null;
  approvedAt?: Date | null;
  approvedById?: string | null;
  fulfilledAt?: Date | null;
  cancelledAt?: Date | null;
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  taxPercent: number;
  taxAmount: number;
  total: number;
  currency: string;
  paymentTermId?: string | null;
  customerReference?: string | null;
  incoterm?: string | null;
  incotermLocation?: string | null;
  shippingAddress?: string | null;
  billingAddress?: string | null;
  terms?: string | null;
  notes?: string | null;
  createdAt: Date;
  updatedAt: Date;
  items?: SalesOrderItem[];
  account?: { id: string; name: string } | null;
  deal?: { id: string; name: string } | null;
  contact?: { id: string; firstName: string; lastName: string } | null;
  quote?: { id: string; quoteNumber: string } | null;
  paymentTerm?: { id: string; name: string; dueDays: number } | null;
}

export interface SalesOrderItem {
  id: string;
  salesOrderId: string;
  productId?: string | null;
  productName?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  totalPrice: number;
  sortOrder: number;
  unitOfMeasure?: string;
}

export const salesOrdersApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<SalesOrder>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<SalesOrder>>(
      `${CRM_BASE}/sales-orders?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<SalesOrder> => {
    const response = await apiClient.get<SalesOrder>(`${CRM_BASE}/sales-orders/${id}`);
    return response;
  },

  getByIdWithDetails: async (id: string): Promise<SalesOrder> => {
    // Backend /sales-orders/:id already includes items — no separate /details endpoint
    const response = await apiClient.get<SalesOrder>(`${CRM_BASE}/sales-orders/${id}`);
    return response;
  },

  create: async (data: Partial<SalesOrder>): Promise<SalesOrder> => {
    const response = await apiClient.post<SalesOrder>(`${CRM_BASE}/sales-orders`, data);
    return response;
  },

  createFromQuote: async (quoteId: string): Promise<SalesOrder> => {
    const response = await apiClient.post<SalesOrder>(
      `${CRM_BASE}/sales-orders/from-quote/${quoteId}`,
    );
    return response;
  },

  update: async (id: string, data: Partial<SalesOrder>): Promise<SalesOrder> => {
    const response = await apiClient.put<SalesOrder>(`${CRM_BASE}/sales-orders/${id}`, data);
    return response;
  },

  updateStatus: async (
    id: string,
    status: SalesOrder['status'],
    rejectionReason?: string,
  ): Promise<SalesOrder> => {
    const response = await apiClient.patch<SalesOrder>(`${CRM_BASE}/sales-orders/${id}/status`, {
      status,
      ...(rejectionReason && { rejectionReason }),
    });
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/sales-orders/${id}`);
  },

  // Sales Order Items
  addItem: async (
    id: string,
    data: {
      productId?: string;
      description: string;
      quantity: number;
      unitPrice?: number;
      discount?: number;
      taxGroupId?: string;
      hsnCode?: string;
      unitOfMeasure?: string;
    },
  ): Promise<SalesOrder> => {
    const response = await apiClient.post<SalesOrder>(`${CRM_BASE}/sales-orders/${id}/items`, data);
    return response;
  },

  updateItem: async (
    id: string,
    itemId: string,
    data: {
      description?: string;
      quantity?: number;
      unitPrice?: number;
      discount?: number;
      taxGroupId?: string;
      hsnCode?: string;
      unitOfMeasure?: string;
    },
  ): Promise<SalesOrder> => {
    const response = await apiClient.put<SalesOrder>(
      `${CRM_BASE}/sales-orders/${id}/items/${itemId}`,
      data,
    );
    return response;
  },

  deleteItem: async (id: string, itemId: string): Promise<SalesOrder> => {
    const response = await apiClient.delete<SalesOrder>(
      `${CRM_BASE}/sales-orders/${id}/items/${itemId}`,
    );
    return response;
  },

  fulfillItems: async (
    id: string,
    data: { items: Array<{ itemId: string; quantity: number }> },
  ): Promise<SalesOrder> => {
    const response = await apiClient.post<SalesOrder>(
      `${CRM_BASE}/sales-orders/${id}/fulfill-items`,
      data,
    );
    return response;
  },

  downloadPdf: async (id: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(`${CRM_BASE}/sales-orders/${id}/pdf`, {
      responseType: 'blob',
    });
    return response;
  },
};

// ============================================================================
// PURCHASE ORDERS API
// ============================================================================
export interface PurchaseOrder {
  id: string;
  tenantId: string;
  poNumber: string;
  vendorId: string;
  requestId?: string | null;
  status:
    | 'DRAFT'
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'REJECTED'
    | 'SENT'
    | 'ACKNOWLEDGED'
    | 'SHIPPED'
    | 'PARTIALLY_RECEIVED'
    | 'RECEIVED'
    | 'CANCELLED';
  orderDate?: Date | null;
  expectedDate?: Date | null;
  receivedDate?: Date | null;
  subtotal: number;
  taxAmount: number;
  total: number;
  currency: string;
  paymentTerms?: string | null;
  notes?: string | null;
  createdBy?: string | null;
  createdAt: Date;
  updatedAt: Date;
  items?: PurchaseOrderItem[];
  vendorName?: string;
}

export interface PurchaseOrderItem {
  id: string;
  purchaseOrderId: string;
  productId?: string | null;
  name: string;
  description?: string | null;
  quantity: number;
  receivedQty: number;
  unitPrice: number;
  totalPrice: number;
}

export const purchaseOrdersApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<PurchaseOrder>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<PurchaseOrder>>(
      `${CRM_BASE}/purchase-orders?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<PurchaseOrder> => {
    const response = await apiClient.get<PurchaseOrder>(`${CRM_BASE}/purchase-orders/${id}`);
    return response;
  },

  getByIdWithDetails: async (id: string): Promise<PurchaseOrder> => {
    const response = await apiClient.get<PurchaseOrder>(
      `${CRM_BASE}/purchase-orders/${id}/details`,
    );
    return response;
  },

  create: async (data: Partial<PurchaseOrder>): Promise<PurchaseOrder> => {
    const response = await apiClient.post<PurchaseOrder>(`${CRM_BASE}/purchase-orders`, data);
    return response;
  },

  update: async (id: string, data: Partial<PurchaseOrder>): Promise<PurchaseOrder> => {
    const response = await apiClient.put<PurchaseOrder>(`${CRM_BASE}/purchase-orders/${id}`, data);
    return response;
  },

  updateStatus: async (id: string, status: PurchaseOrder['status']): Promise<PurchaseOrder> => {
    const response = await apiClient.patch<PurchaseOrder>(
      `${CRM_BASE}/purchase-orders/${id}/status`,
      { status },
    );
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/purchase-orders/${id}`);
  },

  // PO Items
  addItem: async (
    id: string,
    data: {
      productId?: string;
      name: string;
      description?: string;
      quantity: number;
      unitPrice: number;
    },
  ): Promise<PurchaseOrder> => {
    const response = await apiClient.post<PurchaseOrder>(
      `${CRM_BASE}/purchase-orders/${id}/items`,
      data,
    );
    return response;
  },

  updateItem: async (
    id: string,
    itemId: string,
    data: {
      name?: string;
      description?: string;
      quantity?: number;
      unitPrice?: number;
    },
  ): Promise<PurchaseOrder> => {
    const response = await apiClient.put<PurchaseOrder>(
      `${CRM_BASE}/purchase-orders/${id}/items/${itemId}`,
      data,
    );
    return response;
  },

  deleteItem: async (id: string, itemId: string): Promise<PurchaseOrder> => {
    const response = await apiClient.delete<PurchaseOrder>(
      `${CRM_BASE}/purchase-orders/${id}/items/${itemId}`,
    );
    return response;
  },

  receiveItems: async (
    id: string,
    items: { itemId: string; receivedQty: number }[],
  ): Promise<PurchaseOrder> => {
    const response = await apiClient.post<PurchaseOrder>(
      `${CRM_BASE}/purchase-orders/${id}/receive`,
      { items },
    );
    return response;
  },

  downloadPdf: async (id: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(`${CRM_BASE}/purchase-orders/${id}/pdf`, {
      responseType: 'blob',
    });
    return response;
  },
};

// ============================================================================
// INVOICES API
// ============================================================================
export const invoicesApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Invoice>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Invoice>>(
      `${CRM_BASE}/invoices?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Invoice> => {
    const response = await apiClient.get<Invoice>(`${CRM_BASE}/invoices/${id}`);
    return response;
  },

  create: async (data: Partial<Invoice>): Promise<Invoice> => {
    const response = await apiClient.post<Invoice>(`${CRM_BASE}/invoices`, data);
    return response;
  },

  createFromSalesOrder: async (salesOrderId: string): Promise<Invoice> => {
    const response = await apiClient.post<Invoice>(
      `${CRM_BASE}/invoices/from-sales-order/${salesOrderId}`,
    );
    return response;
  },

  update: async (id: string, data: Partial<Invoice>): Promise<Invoice> => {
    const response = await apiClient.put<Invoice>(`${CRM_BASE}/invoices/${id}`, data);
    return response;
  },

  updateStatus: async (id: string, status: string): Promise<Invoice> => {
    const response = await apiClient.patch<Invoice>(`${CRM_BASE}/invoices/${id}/status`, {
      status,
    });
    return response;
  },

  recordPayment: async (
    id: string,
    data: { amount: number; paymentDate?: string; notes?: string },
  ): Promise<Invoice> => {
    const response = await apiClient.post<Invoice>(
      `${CRM_BASE}/invoices/${id}/record-payment`,
      data,
    );
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/invoices/${id}`);
  },

  // Invoice Items
  addItem: async (
    id: string,
    data: {
      productId?: string;
      description: string;
      quantity: number;
      unitPrice: number;
      discount?: number;
      taxGroupId?: string;
      hsnCode?: string;
      unitOfMeasure?: string;
    },
  ): Promise<Invoice> => {
    const response = await apiClient.post<Invoice>(`${CRM_BASE}/invoices/${id}/items`, data);
    return response;
  },

  updateItem: async (
    id: string,
    itemId: string,
    data: {
      productId?: string;
      description?: string;
      quantity?: number;
      unitPrice?: number;
      discount?: number;
      taxGroupId?: string;
      hsnCode?: string;
      unitOfMeasure?: string;
    },
  ): Promise<Invoice> => {
    const response = await apiClient.put<Invoice>(
      `${CRM_BASE}/invoices/${id}/items/${itemId}`,
      data,
    );
    return response;
  },

  removeItem: async (id: string, itemId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/invoices/${id}/items/${itemId}`);
  },

  downloadPdf: async (id: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(`${CRM_BASE}/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response;
  },
};

// ============================================================================
// CASES API
// ============================================================================
export interface Case {
  id: string;
  tenantId: string;
  caseNumber: string;
  subject: string;
  description?: string | null;
  status:
    | 'NEW'
    | 'ASSIGNED'
    | 'IN_PROGRESS'
    | 'PENDING_CUSTOMER'
    | 'ESCALATED'
    | 'RESOLVED'
    | 'CLOSED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  type?: string | null;
  category?: string | null;
  origin?: string | null;
  accountId?: string | null;
  contactId?: string | null;
  ownerId?: string | null;
  owner?: { id: string; firstName: string; lastName: string } | null;
  account?: { id: string; name: string } | null;
  contact?: { id: string; firstName: string; lastName: string } | null;
  slaId?: string | null;
  responseDeadline?: Date | null;
  resolutionDeadline?: Date | null;
  resolution?: string | null;
  firstResponseAt?: Date | null;
  resolvedAt?: Date | null;
  closedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export const casesApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Case>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Case>>(`${CRM_BASE}/cases?${query}`);
    return response;
  },

  getById: async (id: string): Promise<Case> => {
    const response = await apiClient.get<Case>(`${CRM_BASE}/cases/${id}`);
    return response;
  },

  getByIdWithDetails: async (id: string): Promise<Case> => {
    const response = await apiClient.get<Case>(`${CRM_BASE}/cases/${id}/details`);
    return response;
  },

  getStats: async (): Promise<{
    total: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
    avgResolutionTimeHours: number;
  }> => {
    const response = await apiClient.get<{
      total: number;
      byStatus: Record<string, number>;
      byPriority: Record<string, number>;
      avgResolutionTimeHours: number;
    }>(`${CRM_BASE}/cases/stats`);
    return response;
  },

  getCategories: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`${CRM_BASE}/cases/categories`);
    return response;
  },

  getTypes: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`${CRM_BASE}/cases/types`);
    return response;
  },

  create: async (data: Partial<Case>): Promise<Case> => {
    const response = await apiClient.post<Case>(`${CRM_BASE}/cases`, data);
    return response;
  },

  update: async (id: string, data: Partial<Case>): Promise<Case> => {
    const response = await apiClient.put<Case>(`${CRM_BASE}/cases/${id}`, data);
    return response;
  },

  updateStatus: async (id: string, status: Case['status']): Promise<Case> => {
    const response = await apiClient.patch<Case>(`${CRM_BASE}/cases/${id}/status`, { status });
    return response;
  },

  resolve: async (id: string, resolution: string): Promise<Case> => {
    const response = await apiClient.patch<Case>(`${CRM_BASE}/cases/${id}/resolve`, { resolution });
    return response;
  },

  recordFirstResponse: async (id: string): Promise<Case> => {
    const response = await apiClient.patch<Case>(`${CRM_BASE}/cases/${id}/first-response`);
    return response;
  },

  close: async (id: string): Promise<Case> => {
    const response = await apiClient.patch<Case>(`${CRM_BASE}/cases/${id}/close`);
    return response;
  },

  reopen: async (id: string): Promise<Case> => {
    const response = await apiClient.patch<Case>(`${CRM_BASE}/cases/${id}/reopen`);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/cases/${id}`);
  },

  getHistory: async (id: string): Promise<CaseHistoryEntry[]> => {
    const response = await apiClient.get<CaseHistoryEntry[]>(`${CRM_BASE}/cases/${id}/history`);
    return response;
  },
};

export interface CaseHistoryEntry {
  id: string;
  caseId: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  changedById: string | null;
  changedAt: Date;
}

// ============================================================================
// WORKFLOWS API
// ============================================================================
export interface WorkflowAction {
  type: 'SEND_EMAIL' | 'UPDATE_FIELD' | 'CREATE_TASK' | 'SEND_NOTIFICATION' | 'WEBHOOK';
  config: Record<string, unknown>;
}

/**
 * Workflow trigger values — MUST match the backend `WorkflowTrigger` Prisma enum
 * (apps/crm). The UI previously used 'RECORD_CREATED'/'RECORD_UPDATED'/'FIELD_CHANGED',
 * which the backend `@IsEnum(WorkflowTrigger)` rejects with 400 — so workflows could
 * not be created/edited from the UI at all.
 */
export type WorkflowTriggerType =
  | 'ON_CREATE'
  | 'ON_UPDATE'
  | 'ON_FIELD_CHANGE'
  | 'ON_STAGE_CHANGE'
  | 'SCHEDULED'
  | 'MANUAL';

export interface Workflow {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  triggerType: WorkflowTriggerType;
  objectType: 'LEAD' | 'CONTACT' | 'ACCOUNT' | 'DEAL' | 'CASE' | 'TASK';
  isActive: boolean;
  triggerConditions?: Record<string, unknown>;
  actions?: WorkflowAction[];
  executionCount: number;
  lastExecutedAt?: Date | null;
  createdBy?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName: string;
  objectType: string;
  objectId: string;
  triggerType: string;
  triggerData?: Record<string, unknown>;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  actionsExecuted: number;
  actionsTotal: number;
  startedAt: Date;
  completedAt?: Date | null;
  errorMessage?: string | null;
}

export const workflowsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Workflow>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Workflow>>(
      `${CRM_BASE}/workflows?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Workflow> => {
    const response = await apiClient.get<Workflow>(`${CRM_BASE}/workflows/${id}`);
    return response;
  },

  getStats: async (): Promise<{
    total: number;
    active: number;
    byTriggerType: Record<string, number>;
    totalExecutions: number;
  }> => {
    const response = await apiClient.get<{
      total: number;
      active: number;
      byTriggerType: Record<string, number>;
      totalExecutions: number;
    }>(`${CRM_BASE}/workflows/stats`);
    return response;
  },

  create: async (data: Partial<Workflow>): Promise<Workflow> => {
    const response = await apiClient.post<Workflow>(`${CRM_BASE}/workflows`, data);
    return response;
  },

  update: async (id: string, data: Partial<Workflow>): Promise<Workflow> => {
    const response = await apiClient.put<Workflow>(`${CRM_BASE}/workflows/${id}`, data);
    return response;
  },

  toggleActive: async (id: string): Promise<Workflow> => {
    const response = await apiClient.patch<Workflow>(`${CRM_BASE}/workflows/${id}/toggle`);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/workflows/${id}`);
  },

  // Workflow Executions
  getExecutions: async (
    params: QueryParams = {},
  ): Promise<PaginatedResponse<WorkflowExecution>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<WorkflowExecution>>(
      `${CRM_BASE}/workflows/executions?${query}`,
    );
    return response;
  },

  getWorkflowExecutions: async (
    id: string,
    params: QueryParams = {},
  ): Promise<PaginatedResponse<WorkflowExecution>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<WorkflowExecution>>(
      `${CRM_BASE}/workflows/${id}/executions?${query}`,
    );
    return response;
  },

  triggerManually: async (
    id: string,
    data: { objectId: string },
  ): Promise<{ triggered: boolean; executionId: string; workflowName: string }> => {
    const response = await apiClient.post<{
      triggered: boolean;
      executionId: string;
      workflowName: string;
    }>(`${CRM_BASE}/workflows/${id}/trigger`, data);
    return response;
  },
};

// ============================================================================
// ANALYTICS & FORECASTING API
// ============================================================================

// Types for analytics responses
export interface MonthlyRevenueData {
  month: string;
  revenue: number;
  deals: number;
  leads: number;
}

export interface PipelineStageData {
  name: string;
  value: number;
  totalValue: number;
  order: number;
}

export const analyticsApi = {
  getDashboard: async (): Promise<{
    leads: LeadStats;
    deals: DealStats;
    tasks: TaskStats;
    activities: {
      total: number;
      byType: Record<string, number>;
      thisWeek: number;
    };
  }> => {
    const response = await apiClient.get<{
      leads: LeadStats;
      deals: DealStats;
      tasks: TaskStats;
      activities: {
        total: number;
        byType: Record<string, number>;
        thisWeek: number;
      };
    }>(`${CRM_BASE}/analytics/dashboard`);
    return response;
  },

  getRevenueTrend: async (): Promise<{ data: MonthlyRevenueData[] }> => {
    const response = await apiClient.get<{ data: MonthlyRevenueData[] }>(
      `${CRM_BASE}/analytics/revenue-trend`,
    );
    return response;
  },

  getPipelineSummary: async (pipelineId?: string): Promise<{ data: PipelineStageData[] }> => {
    const query = pipelineId ? `?pipelineId=${pipelineId}` : '';
    const response = await apiClient.get<{ data: PipelineStageData[] }>(
      `${CRM_BASE}/analytics/pipeline-summary${query}`,
    );
    return response;
  },

  getRevenueForecast: async (params: {
    startDate?: string;
    endDate?: string;
    pipelineId?: string;
  }): Promise<{
    totalForecast: number;
    closedWon: number;
    pipeline: number;
    byMonth: Array<{ month: string; forecast: number; actual: number }>;
    byStage: Array<{ stage: string; amount: number; probability: number }>;
  }> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<{
      totalForecast: number;
      closedWon: number;
      pipeline: number;
      byMonth: Array<{ month: string; forecast: number; actual: number }>;
      byStage: Array<{ stage: string; amount: number; probability: number }>;
    }>(`${CRM_BASE}/analytics/revenue-forecast?${query}`);
    return response;
  },

  getLeadConversion: async (): Promise<{
    totalLeads: number;
    converted: number;
    conversionRate: number;
    avgConversionTime: number;
    bySource: Array<{ source: string; total: number; converted: number; rate: number }>;
  }> => {
    const response = await apiClient.get<{
      totalLeads: number;
      converted: number;
      conversionRate: number;
      avgConversionTime: number;
      bySource: Array<{ source: string; total: number; converted: number; rate: number }>;
    }>(`${CRM_BASE}/analytics/lead-conversion`);
    return response;
  },

  getSalesFunnel: async (
    pipelineId?: string,
  ): Promise<{
    stages: Array<{
      stage: string;
      deals: number;
      value: number;
      conversionRate: number;
    }>;
    totalValue: number;
    avgDealSize: number;
  }> => {
    const query = pipelineId ? `?pipelineId=${pipelineId}` : '';
    const response = await apiClient.get<{
      stages: Array<{
        stage: string;
        deals: number;
        value: number;
        conversionRate: number;
      }>;
      totalValue: number;
      avgDealSize: number;
    }>(`${CRM_BASE}/analytics/sales-funnel${query}`);
    return response;
  },

  getActivityMetrics: async (params: {
    startDate?: string;
    endDate?: string;
    ownerId?: string;
  }): Promise<{
    totalActivities: number;
    completed: number;
    completionRate: number;
    byType: Record<string, number>;
    byUser: Array<{ userId: string; userName: string; count: number }>;
    trends: Array<{ date: string; count: number }>;
  }> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<{
      totalActivities: number;
      completed: number;
      completionRate: number;
      byType: Record<string, number>;
      byUser: Array<{ userId: string; userName: string; count: number }>;
      trends: Array<{ date: string; count: number }>;
    }>(`${CRM_BASE}/analytics/activity-metrics?${query}`);
    return response;
  },
};

// ============================================================================
// BULK OPERATIONS API
// ============================================================================
export const bulkOperationsApi = {
  // Bulk Accounts
  bulkUpdateAccounts: async (ids: string[], data: Partial<Account>): Promise<{ count: number }> => {
    const response = await apiClient.patch<{ count: number }>(`${CRM_BASE}/accounts/bulk/update`, {
      ids,
      data,
    });
    return response;
  },

  bulkDeleteAccounts: async (ids: string[]): Promise<{ deleted: number; skipped: string[] }> => {
    const response = await apiClient.post<{ deleted: number; skipped: string[] }>(
      `${CRM_BASE}/accounts/bulk/delete`,
      { ids },
    );
    return response;
  },

  exportAccounts: async (ids?: string[]): Promise<Blob> => {
    const response = await apiClient.post<Blob>(
      `${CRM_BASE}/accounts/export/csv`,
      { ids },
      { responseType: 'blob' },
    );
    return response;
  },

  // Bulk Contacts
  bulkUpdateContacts: async (ids: string[], data: Partial<Contact>): Promise<{ count: number }> => {
    const response = await apiClient.patch<{ count: number }>(`${CRM_BASE}/contacts/bulk/update`, {
      ids,
      data,
    });
    return response;
  },

  bulkDeleteContacts: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(`${CRM_BASE}/contacts/bulk/delete`, {
      ids,
    });
    return response;
  },

  exportContacts: async (ids?: string[]): Promise<Blob> => {
    const response = await apiClient.post<Blob>(
      `${CRM_BASE}/contacts/export/csv`,
      { ids },
      { responseType: 'blob' },
    );
    return response;
  },

  // Bulk Tasks
  bulkUpdateTasks: async (ids: string[], data: Partial<Task>): Promise<{ count: number }> => {
    const response = await apiClient.patch<{ count: number }>(`${CRM_BASE}/tasks/bulk/update`, {
      ids,
      data,
    });
    return response;
  },

  bulkCompleteTasks: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.patch<{ count: number }>(`${CRM_BASE}/tasks/bulk/complete`, {
      ids,
    });
    return response;
  },

  bulkDeleteTasks: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(`${CRM_BASE}/tasks/bulk/delete`, {
      ids,
    });
    return response;
  },

  // Bulk Activities
  bulkDeleteActivities: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(`${CRM_BASE}/activities/bulk/delete`, {
      ids,
    });
    return response;
  },

  // Bulk Cases
  bulkUpdateCases: async (ids: string[], data: Partial<Case>): Promise<{ count: number }> => {
    const response = await apiClient.patch<{ count: number }>(`${CRM_BASE}/cases/bulk/update`, {
      ids,
      data,
    });
    return response;
  },

  bulkCloseCases: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.patch<{ count: number }>(`${CRM_BASE}/cases/bulk/close`, {
      ids,
    });
    return response;
  },

  bulkDeleteCases: async (ids: string[]): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(`${CRM_BASE}/cases/bulk/delete`, {
      ids,
    });
    return response;
  },
};

// ============================================================================
// DUPLICATES API
// ============================================================================
export const duplicatesApi = {
  findLeadDuplicates: async (leadId: string, threshold?: number): Promise<DuplicateResult[]> => {
    const query = threshold !== undefined ? `?threshold=${threshold}` : '';
    const response = await apiClient.get<DuplicateResult[]>(
      `${CRM_BASE}/duplicates/leads/${leadId}${query}`,
    );
    return response;
  },

  findContactDuplicates: async (
    contactId: string,
    threshold?: number,
  ): Promise<DuplicateResult[]> => {
    const query = threshold !== undefined ? `?threshold=${threshold}` : '';
    const response = await apiClient.get<DuplicateResult[]>(
      `${CRM_BASE}/duplicates/contacts/${contactId}${query}`,
    );
    return response;
  },

  findAccountDuplicates: async (
    accountId: string,
    threshold?: number,
  ): Promise<DuplicateResult[]> => {
    const query = threshold !== undefined ? `?threshold=${threshold}` : '';
    const response = await apiClient.get<DuplicateResult[]>(
      `${CRM_BASE}/duplicates/accounts/${accountId}${query}`,
    );
    return response;
  },

  checkIncomingLead: async (
    data: CheckDuplicateInput,
    threshold?: number,
  ): Promise<DuplicateResult[]> => {
    const query = threshold !== undefined ? `?threshold=${threshold}` : '';
    const response = await apiClient.post<DuplicateResult[]>(
      `${CRM_BASE}/duplicates/leads/check${query}`,
      data,
    );
    return response;
  },

  mergeLeads: async (data: MergeRecordsInput): Promise<void> => {
    await apiClient.post<void>(`${CRM_BASE}/duplicates/leads/merge`, data);
  },

  mergeContacts: async (data: MergeRecordsInput): Promise<void> => {
    await apiClient.post<void>(`${CRM_BASE}/duplicates/contacts/merge`, data);
  },

  mergeAccounts: async (data: MergeRecordsInput): Promise<void> => {
    await apiClient.post<void>(`${CRM_BASE}/duplicates/accounts/merge`, data);
  },
};

// ============================================================================
// LEAD SCORING API
// ============================================================================
export const leadScoringApi = {
  getRules: async (): Promise<LeadScoringRule[]> => {
    const response = await apiClient.get<LeadScoringRule[]>(`${CRM_BASE}/lead-scoring/rules`);
    return response;
  },

  createRule: async (data: Partial<LeadScoringRule>): Promise<LeadScoringRule> => {
    const response = await apiClient.post<LeadScoringRule>(`${CRM_BASE}/lead-scoring/rules`, data);
    return response;
  },

  updateRule: async (id: string, data: Partial<LeadScoringRule>): Promise<LeadScoringRule> => {
    const response = await apiClient.put<LeadScoringRule>(
      `${CRM_BASE}/lead-scoring/rules/${id}`,
      data,
    );
    return response;
  },

  deleteRule: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/lead-scoring/rules/${id}`);
  },

  seedDefaults: async (): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(
      `${CRM_BASE}/lead-scoring/rules/seed-defaults`,
    );
    return response;
  },

  rescoreAll: async (): Promise<{ scored: number }> => {
    const response = await apiClient.post<{ scored: number }>(
      `${CRM_BASE}/lead-scoring/rules/rescore-all`,
    );
    return response;
  },

  getScoreBreakdown: async (
    leadId: string,
  ): Promise<{ ruleName: string; ruleType: string; points: number; matched: boolean }[]> => {
    const response = await apiClient.get<
      { ruleName: string; ruleType: string; points: number; matched: boolean }[]
    >(`${CRM_BASE}/lead-scoring/rules/breakdown/${leadId}`);
    return response;
  },

  getLeadScoreHistory: async (leadId: string): Promise<ScoreHistoryEntry[]> => {
    const response = await apiClient.get<ScoreHistoryEntry[]>(
      `${CRM_BASE}/lead-scoring/rules/history/lead/${leadId}`,
    );
    return response;
  },

  getContactScoreHistory: async (contactId: string): Promise<ScoreHistoryEntry[]> => {
    const response = await apiClient.get<ScoreHistoryEntry[]>(
      `${CRM_BASE}/lead-scoring/rules/history/contact/${contactId}`,
    );
    return response;
  },

  getEmailEngagement: async (leadId: string): Promise<EmailEngagementSummary> => {
    const response = await apiClient.get<EmailEngagementSummary>(
      `${CRM_BASE}/lead-scoring/rules/engagement/${leadId}`,
    );
    return response;
  },
};

export interface EmailEngagementSummary {
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  spam: number;
  unsubscribed: number;
  lastEngagementAt: string | null;
}

export interface ScoreHistoryEntry {
  id: string;
  leadId: string | null;
  contactId: string | null;
  email: string | null;
  phone: string | null;
  score: number;
  rating: string | null;
  breakdown: Record<string, unknown> | null;
  triggeringRule: string | null;
  reason: string | null;
  recordedAt: string;
}

// ============================================================================
// WEBHOOKS API
// ============================================================================
export const webhooksApi = {
  getAll: async (): Promise<WebhookEndpoint[]> => {
    const response = await apiClient.get<WebhookEndpoint[]>(`${CRM_BASE}/webhooks`);
    return response;
  },

  getById: async (id: string): Promise<WebhookEndpoint> => {
    const response = await apiClient.get<WebhookEndpoint>(`${CRM_BASE}/webhooks/${id}`);
    return response;
  },

  create: async (data: Partial<WebhookEndpoint>): Promise<WebhookEndpoint> => {
    const response = await apiClient.post<WebhookEndpoint>(`${CRM_BASE}/webhooks`, data);
    return response;
  },

  update: async (id: string, data: Partial<WebhookEndpoint>): Promise<WebhookEndpoint> => {
    const response = await apiClient.put<WebhookEndpoint>(`${CRM_BASE}/webhooks/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/webhooks/${id}`);
  },

  rotateSecret: async (id: string): Promise<{ secret: string }> => {
    const response = await apiClient.post<{ secret: string }>(
      `${CRM_BASE}/webhooks/${id}/rotate-secret`,
    );
    return response;
  },

  getSecret: async (id: string): Promise<{ secret: string }> => {
    const response = await apiClient.get<{ secret: string }>(`${CRM_BASE}/webhooks/${id}/secret`);
    return response;
  },

  getDeliveries: async (
    id: string,
    params: QueryParams = {},
  ): Promise<PaginatedResponse<WebhookDelivery>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<WebhookDelivery>>(
      `${CRM_BASE}/webhooks/${id}/deliveries?${query}`,
    );
    return response;
  },
};

// ============================================================================
// ACCOUNT CONTACTS API
// ============================================================================
export const accountContactsApi = {
  getAccountContacts: async (accountId: string): Promise<AccountContact[]> => {
    const response = await apiClient.get<AccountContact[]>(
      `${CRM_BASE}/accounts/${accountId}/contacts`,
    );
    return response;
  },

  addContact: async (
    accountId: string,
    data: { contactId: string; role: AccountContactRole; isPrimary?: boolean },
  ): Promise<AccountContact> => {
    const response = await apiClient.post<AccountContact>(
      `${CRM_BASE}/accounts/${accountId}/contacts`,
      data,
    );
    return response;
  },

  updateContact: async (
    accountId: string,
    contactId: string,
    data: { role?: AccountContactRole; isPrimary?: boolean },
  ): Promise<AccountContact> => {
    const response = await apiClient.put<AccountContact>(
      `${CRM_BASE}/accounts/${accountId}/contacts/${contactId}`,
      data,
    );
    return response;
  },

  removeContact: async (accountId: string, contactId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/accounts/${accountId}/contacts/${contactId}`);
  },

  setPrimary: async (accountId: string, contactId: string): Promise<AccountContact> => {
    const response = await apiClient.patch<AccountContact>(
      `${CRM_BASE}/accounts/${accountId}/contacts/${contactId}/primary`,
    );
    return response;
  },
};

// ============================================================================
// APPROVALS API
// ============================================================================
export const approvalsApi = {
  // Approval requests
  getPending: async (): Promise<ApprovalRequest[]> => {
    const response = await apiClient.get<ApprovalRequest[]>(`${CRM_BASE}/approvals/pending`);
    return response;
  },

  approve: async (id: string, comments?: string): Promise<ApprovalRequest> => {
    const response = await apiClient.post<ApprovalRequest>(`${CRM_BASE}/approvals/${id}/approve`, {
      comments,
    });
    return response;
  },

  reject: async (id: string, comments?: string): Promise<ApprovalRequest> => {
    const response = await apiClient.post<ApprovalRequest>(`${CRM_BASE}/approvals/${id}/reject`, {
      comments,
    });
    return response;
  },

  cancel: async (id: string): Promise<ApprovalRequest> => {
    const response = await apiClient.post<ApprovalRequest>(`${CRM_BASE}/approvals/${id}/cancel`);
    return response;
  },

  getByEntity: async (entityType: string, entityId: string): Promise<ApprovalRequest[]> => {
    const response = await apiClient.get<ApprovalRequest[]>(
      `${CRM_BASE}/approvals/entity/${entityType}/${entityId}`,
    );
    return response;
  },

  // Approval workflows
  getWorkflows: async (entityType?: string): Promise<ApprovalWorkflow[]> => {
    const query = entityType ? `?entityType=${entityType}` : '';
    const response = await apiClient.get<ApprovalWorkflow[]>(
      `${CRM_BASE}/approvals/workflows${query}`,
    );
    return response;
  },

  getWorkflowById: async (id: string): Promise<ApprovalWorkflow> => {
    const response = await apiClient.get<ApprovalWorkflow>(`${CRM_BASE}/approvals/workflows/${id}`);
    return response;
  },

  createWorkflow: async (data: Partial<ApprovalWorkflow>): Promise<ApprovalWorkflow> => {
    const response = await apiClient.post<ApprovalWorkflow>(
      `${CRM_BASE}/approvals/workflows`,
      data,
    );
    return response;
  },

  updateWorkflow: async (
    id: string,
    data: Partial<ApprovalWorkflow>,
  ): Promise<ApprovalWorkflow> => {
    const response = await apiClient.put<ApprovalWorkflow>(
      `${CRM_BASE}/approvals/workflows/${id}`,
      data,
    );
    return response;
  },

  deleteWorkflow: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/approvals/workflows/${id}`);
  },

  submit: async (data: { entityType: string; entityId: string }): Promise<ApprovalRequest> => {
    const response = await apiClient.post<ApprovalRequest>(`${CRM_BASE}/approvals/submit`, data);
    return response;
  },
};

// ============================================================================
// INTAKE FORMS API — Cycle 50 Universal Public Lead Intake
// ============================================================================
export interface IntakeForm {
  id: string;
  tenantId: string;
  formKey: string;
  name: string;
  description?: string | null;
  fields: string[];
  captchaEnabled: boolean;
  rateLimitPerMinute: number;
  isActive: boolean;
  successRedirectUrl?: string | null;
  campaignId?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateIntakeFormInput {
  name: string;
  formKey?: string;
  description?: string;
  fields: string[];
  captchaEnabled?: boolean;
  rateLimitPerMinute?: number;
  isActive?: boolean;
  successRedirectUrl?: string;
  campaignId?: string;
}

export interface UpdateIntakeFormInput extends Partial<CreateIntakeFormInput> {
  version: number;
}

export const intakeFormsApi = {
  getAll: async (): Promise<IntakeForm[]> => {
    return apiClient.get<IntakeForm[]>(`${CRM_BASE}/intake-forms`);
  },

  getById: async (id: string): Promise<IntakeForm> => {
    return apiClient.get<IntakeForm>(`${CRM_BASE}/intake-forms/${id}`);
  },

  create: async (data: CreateIntakeFormInput): Promise<IntakeForm> => {
    return apiClient.post<IntakeForm>(`${CRM_BASE}/intake-forms`, data);
  },

  update: async (id: string, data: UpdateIntakeFormInput): Promise<IntakeForm> => {
    return apiClient.put<IntakeForm>(`${CRM_BASE}/intake-forms/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/intake-forms/${id}`);
  },
};

// ============================================================================
// SLA API
// ============================================================================
export interface SLA {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  status: string;
  businessHours: Record<string, unknown>;
  targets: Array<{
    priority: string;
    metricType: string;
    targetMinutes: number;
  }>;
  escalationRules: Array<{
    thresholdPercent: number;
    notifyRoles: string[];
    action: string;
  }>;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface SlaCompliance {
  withinSla: boolean;
  firstResponse: {
    targetMinutes: number;
    actualMinutes: number | null;
    breached: boolean;
  } | null;
  resolution: {
    targetMinutes: number;
    actualMinutes: number | null;
    breached: boolean;
  } | null;
}

export const slaApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<SLA>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<SLA>>(`${CRM_BASE}/sla?${query}`);
    return response;
  },

  getById: async (id: string): Promise<SLA> => {
    const response = await apiClient.get<SLA>(`${CRM_BASE}/sla/${id}`);
    return response;
  },

  create: async (data: Partial<SLA>): Promise<SLA> => {
    const response = await apiClient.post<SLA>(`${CRM_BASE}/sla`, data);
    return response;
  },

  update: async (id: string, data: Partial<SLA>): Promise<SLA> => {
    const response = await apiClient.put<SLA>(`${CRM_BASE}/sla/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/sla/${id}`);
  },

  checkCompliance: async (slaId: string, caseId: string): Promise<SlaCompliance> => {
    const response = await apiClient.post<SlaCompliance>(
      `${CRM_BASE}/sla/${slaId}/check-compliance/${caseId}`,
    );
    return response;
  },
};

// ============================================================================
// CONTRACTS API
// ============================================================================
export interface Contract {
  id: string;
  tenantId: string;
  contractNumber: string;
  name: string;
  description?: string | null;
  type: string;
  status: string;
  accountId?: string | null;
  dealId?: string | null;
  startDate: Date;
  endDate: Date;
  renewalDate?: Date | null;
  totalValue?: number | null;
  currency: string;
  autoRenew: boolean;
  renewalTermMonths?: number | null;
  renewalNoticeDays: number;
  terms?: string | null;
  ownerId?: string | null;
  slaId?: string | null;
  sla?: { id: string; name: string } | null;
  createdAt: Date;
  updatedAt: Date;
  account?: { id: string; name: string } | null;
  deal?: { id: string; name: string } | null;
  owner?: { id: string; firstName: string; lastName: string; email: string } | null;
  lineItems?: ContractLineItem[];
  amendments?: ContractAmendment[];
}

export interface ContractLineItem {
  id: string;
  productId?: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  sortOrder: number;
}

export interface ContractAmendment {
  id: string;
  contractId: string;
  version: number;
  description: string;
  changes: Record<string, unknown>;
  status: string;
  effectiveDate?: Date | null;
  approvedById?: string | null;
  approvedAt?: Date | null;
  rejectedById?: string | null;
  rejectedAt?: Date | null;
  rejectionReason?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export const contractsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Contract>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Contract>>(
      `${CRM_BASE}/contracts?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Contract> => {
    const response = await apiClient.get<Contract>(`${CRM_BASE}/contracts/${id}`);
    return response;
  },

  create: async (data: Partial<Contract>): Promise<Contract> => {
    const response = await apiClient.post<Contract>(`${CRM_BASE}/contracts`, data);
    return response;
  },

  update: async (id: string, data: Partial<Contract>): Promise<Contract> => {
    const response = await apiClient.put<Contract>(`${CRM_BASE}/contracts/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/contracts/${id}`);
  },

  renew: async (id: string): Promise<Contract> => {
    const response = await apiClient.post<Contract>(`${CRM_BASE}/contracts/${id}/renew`);
    return response;
  },

  getAmendments: async (id: string): Promise<ContractAmendment[]> => {
    const response = await apiClient.get<ContractAmendment[]>(
      `${CRM_BASE}/contracts/${id}/amendments`,
    );
    return response;
  },

  createAmendment: async (
    id: string,
    data: { description: string; changes: Record<string, unknown>; effectiveDate?: string },
  ): Promise<ContractAmendment> => {
    const response = await apiClient.post<ContractAmendment>(
      `${CRM_BASE}/contracts/${id}/amendments`,
      data,
    );
    return response;
  },

  approveAmendment: async (contractId: string, amendmentId: string): Promise<ContractAmendment> => {
    const response = await apiClient.patch<ContractAmendment>(
      `${CRM_BASE}/contracts/${contractId}/amendments/${amendmentId}/approve`,
    );
    return response;
  },

  rejectAmendment: async (
    contractId: string,
    amendmentId: string,
    reason: string,
  ): Promise<ContractAmendment> => {
    const response = await apiClient.patch<ContractAmendment>(
      `${CRM_BASE}/contracts/${contractId}/amendments/${amendmentId}/reject`,
      { reason },
    );
    return response;
  },
};

// ============================================================================
// FORECASTS API
// ============================================================================
export interface Forecast {
  id: string;
  tenantId: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  ownerId: string;
  ownerName?: string | null;
  territoryId?: string | null;
  territoryName?: string | null;
  quota: number;
  pipeline: number;
  bestCase: number;
  commit: number;
  closedWon: number;
  managerOverride?: number | null;
  currency: string;
  createdAt: string;
  updatedAt: string;
}

export interface ForecastRollup {
  periodType: string;
  periodStart: string;
  periodEnd: string;
  totalQuota: number;
  totalPipeline: number;
  totalBestCase: number;
  totalCommit: number;
  totalClosedWon: number;
  forecasts: Forecast[];
}

export const forecastsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Forecast>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Forecast>>(
      `${CRM_BASE}/forecasts?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Forecast> => {
    const response = await apiClient.get<Forecast>(`${CRM_BASE}/forecasts/${id}`);
    return response;
  },

  create: async (data: Partial<Forecast>): Promise<Forecast> => {
    const response = await apiClient.post<Forecast>(`${CRM_BASE}/forecasts`, data);
    return response;
  },

  update: async (id: string, data: Partial<Forecast>): Promise<Forecast> => {
    const response = await apiClient.put<Forecast>(`${CRM_BASE}/forecasts/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/forecasts/${id}`);
  },

  setManagerOverride: async (id: string, managerOverride: number | null): Promise<Forecast> => {
    const response = await apiClient.patch<Forecast>(`${CRM_BASE}/forecasts/${id}/override`, {
      managerOverride,
    });
    return response;
  },

  getRollup: async (
    periodType: string,
    periodStart: string,
    periodEnd: string,
  ): Promise<ForecastRollup> => {
    const query = buildQueryString({ periodType, periodStart, periodEnd });
    const response = await apiClient.get<ForecastRollup>(`${CRM_BASE}/forecasts/rollup?${query}`);
    return response;
  },
};

// ============================================================================
// TERRITORIES API
// ============================================================================
export interface Territory {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  description?: string | null;
  status: string;
  parentId?: string | null;
  parentName?: string | null;
  assignmentRules: Array<Record<string, unknown>>;
  regions: string[];
  countries: string[];
  assignments?: TerritoryAssignment[];
  childCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TerritoryAssignment {
  id: string;
  territoryId: string;
  employeeId: string;
  employeeName?: string | null;
  isPrimary: boolean;
  validFrom: string;
  validTo?: string | null;
  createdAt: string;
}

export const territoriesApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Territory>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Territory>>(
      `${CRM_BASE}/territories?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Territory> => {
    const response = await apiClient.get<Territory>(`${CRM_BASE}/territories/${id}`);
    return response;
  },

  create: async (data: Partial<Territory>): Promise<Territory> => {
    const response = await apiClient.post<Territory>(`${CRM_BASE}/territories`, data);
    return response;
  },

  update: async (id: string, data: Partial<Territory>): Promise<Territory> => {
    const response = await apiClient.put<Territory>(`${CRM_BASE}/territories/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/territories/${id}`);
  },

  createAssignment: async (
    territoryId: string,
    data: { employeeId: string; isPrimary?: boolean; validFrom: string; validTo?: string },
  ): Promise<TerritoryAssignment> => {
    const response = await apiClient.post<TerritoryAssignment>(
      `${CRM_BASE}/territories/${territoryId}/assignments`,
      data,
    );
    return response;
  },

  deleteAssignment: async (territoryId: string, assignmentId: string): Promise<void> => {
    await apiClient.delete<void>(
      `${CRM_BASE}/territories/${territoryId}/assignments/${assignmentId}`,
    );
  },

  getAccounts: async (
    territoryId: string,
    params: QueryParams = {},
  ): Promise<PaginatedResponse<{ id: string; name: string; type: string; industry: string }>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<
      PaginatedResponse<{ id: string; name: string; type: string; industry: string }>
    >(`${CRM_BASE}/territories/${territoryId}/accounts?${query}`);
    return response;
  },
};

// ============================================================================
// COMPETITORS API
// ============================================================================
export interface Competitor {
  id: string;
  tenantId: string;
  name: string;
  website?: string | null;
  description?: string | null;
  strengths: string[];
  weaknesses: string[];
  products: string[];
  marketPosition?: string | null;
  threatLevel: string;
  isActive: boolean;
  dealCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface DealCompetitor {
  id: string;
  dealId: string;
  dealName?: string | null;
  competitorId: string;
  competitorName?: string | null;
  isPrimary: boolean;
  threatLevel: string;
  notes?: string | null;
  outcome?: string | null;
  createdAt: string;
}

export interface CompetitorAnalytics {
  competitorId: string;
  competitorName: string;
  totalDeals: number;
  wonAgainst: number;
  lostTo: number;
  pending: number;
  winRate: number;
}

export const competitorsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Competitor>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Competitor>>(
      `${CRM_BASE}/competitors?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Competitor> => {
    const response = await apiClient.get<Competitor>(`${CRM_BASE}/competitors/${id}`);
    return response;
  },

  create: async (data: Partial<Competitor>): Promise<Competitor> => {
    const response = await apiClient.post<Competitor>(`${CRM_BASE}/competitors`, data);
    return response;
  },

  update: async (id: string, data: Partial<Competitor>): Promise<Competitor> => {
    const response = await apiClient.put<Competitor>(`${CRM_BASE}/competitors/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/competitors/${id}`);
  },

  getDeals: async (
    competitorId: string,
    params: QueryParams = {},
  ): Promise<PaginatedResponse<DealCompetitor>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<DealCompetitor>>(
      `${CRM_BASE}/competitors/${competitorId}/deals?${query}`,
    );
    return response;
  },

  getAnalytics: async (): Promise<CompetitorAnalytics[]> => {
    const response = await apiClient.get<CompetitorAnalytics[]>(
      `${CRM_BASE}/competitors/analytics`,
    );
    return response;
  },

  addToDeal: async (
    dealId: string,
    data: { competitorId: string; isPrimary?: boolean; threatLevel?: string; notes?: string },
  ): Promise<DealCompetitor> => {
    const response = await apiClient.post<DealCompetitor>(
      `${CRM_BASE}/deals/${dealId}/competitors`,
      data,
    );
    return response;
  },

  getDealCompetitors: async (dealId: string): Promise<DealCompetitor[]> => {
    const response = await apiClient.get<DealCompetitor[]>(
      `${CRM_BASE}/deals/${dealId}/competitors`,
    );
    return response;
  },

  removeFromDeal: async (dealId: string, dealCompetitorId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/deals/${dealId}/competitors/${dealCompetitorId}`);
  },
};

// ============================================================================
// SEARCH API (Sprint 10)
// ============================================================================

export interface SearchResultItem {
  id: string;
  entityType: string;
  title: string;
  subtitle?: string;
  status?: string;
  email?: string;
  company?: string;
}

export interface SearchResponse {
  results: SearchResultItem[];
  total: number;
  query: string;
}

// ============================================================================
// CRM Settings
// ============================================================================

export interface CRMSettingsResponse {
  id: string;
  tenantId: string;
  defaultLeadOwner?: string | null;
  leadAutoAssignment: boolean;
  leadAssignmentRule?: Record<string, unknown> | null;
  defaultPipelineId?: string | null;
  dealRotting?: number | null;
  defaultCurrency: string;
  leadNumberPrefix: string;
  dealNumberPrefix: string;
  quoteNumberPrefix: string;
  caseNumberPrefix: string;
  poNumberPrefix: string;
  contractNumberPrefix: string;
  notifyOnLeadAssignment: boolean;
  notifyOnDealStageChange: boolean;
  notifyOnTaskDue: boolean;
  notifyOnCaseAssignment: boolean;
  preferExistingAccountOwner: boolean;
  leadScoringEnabled: boolean;
  scoringThresholdHot: number;
  scoringThresholdWarm: number;
  scoringDecayFloor: number;
  notifyOnLeadHotCrossing: boolean;
  notifyOnLeadCoolingCrossing: boolean;
  enableHotLeadSlaEscalation: boolean;
  enableLeadAutoStatus: boolean;
  importAccountAutoCreate: boolean;
  defaultOwnerFromAccount: boolean;
  inboundEmailEnabled: boolean;
  inboundEmailAddress?: string | null;
  allowBackwardStageMove: boolean;
  allowForwardStageSkip: boolean;
  // Financial settings
  paymentNumberPrefix: string;
  creditNoteNumberPrefix: string;
  baseCurrency: string;
  enableMultiCurrency: boolean;
  // Tax settings
  taxRegimeId?: string | null;
  sellerStateCode?: string | null;
  taxRegistrationNo?: string | null;
  enableItemLevelTax: boolean;
  // Module toggles (inventory + transaction layer)
  enableInventory: boolean;
  enableBookings: boolean;
  enableOnlinePayments: boolean;
  enablePaymentReminders: boolean;
  requireKycForBooking: boolean;
  enablePartners: boolean;
  enableBuyerPortal: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCRMSettingsInput {
  defaultLeadOwner?: string;
  leadAutoAssignment?: boolean;
  leadAssignmentRule?: Record<string, unknown>;
  defaultPipelineId?: string;
  dealRotting?: number;
  defaultCurrency?: string;
  leadNumberPrefix?: string;
  dealNumberPrefix?: string;
  quoteNumberPrefix?: string;
  caseNumberPrefix?: string;
  poNumberPrefix?: string;
  contractNumberPrefix?: string;
  notifyOnLeadAssignment?: boolean;
  notifyOnDealStageChange?: boolean;
  notifyOnTaskDue?: boolean;
  notifyOnCaseAssignment?: boolean;
  preferExistingAccountOwner?: boolean;
  leadScoringEnabled?: boolean;
  scoringThresholdHot?: number;
  scoringThresholdWarm?: number;
  scoringDecayFloor?: number;
  notifyOnLeadHotCrossing?: boolean;
  notifyOnLeadCoolingCrossing?: boolean;
  enableHotLeadSlaEscalation?: boolean;
  enableLeadAutoStatus?: boolean;
  importAccountAutoCreate?: boolean;
  defaultOwnerFromAccount?: boolean;
  inboundEmailEnabled?: boolean;
  inboundEmailAddress?: string;
  allowBackwardStageMove?: boolean;
  allowForwardStageSkip?: boolean;
  // Financial settings
  paymentNumberPrefix?: string;
  creditNoteNumberPrefix?: string;
  baseCurrency?: string;
  enableMultiCurrency?: boolean;
  // Tax settings
  taxRegimeId?: string;
  sellerStateCode?: string;
  taxRegistrationNo?: string;
  enableItemLevelTax?: boolean;
  enableInventory?: boolean;
  enableBookings?: boolean;
  enableOnlinePayments?: boolean;
  enablePaymentReminders?: boolean;
  requireKycForBooking?: boolean;
  enablePartners?: boolean;
  enableProjects?: boolean;
  enableBuyerPortal?: boolean;
}

/**
 * BUG-CRM-19 — Narrow projection used by the lead/contact score badge.
 * Mirrors `PublicScoringConfigDto` in the CRM service. Sales reps can read
 * this; they cannot read the full settings record (which leaks 40+ admin
 * fields).
 */
export interface PublicScoringConfig {
  leadScoringEnabled: boolean;
  scoringThresholdHot: number;
  scoringThresholdWarm: number;
  /** Inventory layer flag (v1.4 FR-37) — gates the business-line switcher. */
  enableInventory: boolean;
  /** Transaction-layer flag — gates charge/payment-plan/booking surfaces. */
  enableBookings: boolean;
  enableOnlinePayments: boolean;
  enablePaymentReminders: boolean;
  requireKycForBooking: boolean;
  /** Brokers (channel partners) flag — gates broker pickers and the Brokers page. */
  enablePartners: boolean;
  /** Projects flag (ADR 2026-09-05 S1) — gates the Projects module. */
  enableProjects: boolean;
}

export const settingsApi = {
  get: async (): Promise<CRMSettingsResponse> => {
    const response = await apiClient.get<CRMSettingsResponse>(`${CRM_BASE}/settings`);
    return response;
  },

  /**
   * BUG-CRM-19 — Use this on pages where non-admin roles need the scoring
   * thresholds (LeadDetailPage, ContactDetailPage). Calling `get()` from
   * those pages would 403 for sales_rep after the permission demotion.
   */
  getPublicConfig: async (): Promise<PublicScoringConfig> => {
    const response = await apiClient.get<PublicScoringConfig>(`${CRM_BASE}/settings/public-config`);
    return response;
  },

  update: async (data: UpdateCRMSettingsInput): Promise<CRMSettingsResponse> => {
    const response = await apiClient.put<CRMSettingsResponse>(`${CRM_BASE}/settings`, data);
    return response;
  },
};

// ---- Attachment types (inline — not in crm.common) ----
export interface CRMAttachment {
  /** R4 (E7): pack-declared document type; null = untyped. */
  documentType?: string | null;
  id: string;
  entityType: string;
  entityId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedById?: string | null;
  createdAt: string;
}

export interface PresignedUploadResponse {
  attachmentId: string;
  uploadUrl: string;
  fileKey: string;
  expiresAt: string;
}

export interface AttachmentDownloadResponse {
  url: string;
  expiresAt: string;
}

export const attachmentsApi = {
  list: async (
    entityType: string,
    entityId: string,
    documentType?: string,
  ): Promise<CRMAttachment[]> => {
    const suffix = documentType ? `?documentType=${encodeURIComponent(documentType)}` : '';
    const response = await apiClient.get<CRMAttachment[]>(
      `${CRM_BASE}/attachments/${entityType}/${entityId}${suffix}`,
    );
    return response;
  },

  /** R4 (E7): set or clear the pack-declared document type. */
  setDocumentType: async (id: string, documentType: string | null): Promise<CRMAttachment> => {
    return apiClient.patch<CRMAttachment>(`${CRM_BASE}/attachments/${id}`, { documentType });
  },

  presign: async (
    entityType: string,
    entityId: string,
    data: { fileName: string; contentType: string; fileSize: number },
  ): Promise<PresignedUploadResponse> => {
    const response = await apiClient.post<PresignedUploadResponse>(
      `${CRM_BASE}/attachments/${entityType}/${entityId}/presign`,
      data,
    );
    return response;
  },

  confirm: async (
    entityType: string,
    entityId: string,
    data: {
      fileKey: string;
      fileName: string;
      contentType: string;
      fileSize: number;
      documentType?: string;
    },
  ): Promise<CRMAttachment> => {
    const response = await apiClient.post<CRMAttachment>(
      `${CRM_BASE}/attachments/${entityType}/${entityId}/confirm`,
      data,
    );
    return response;
  },

  getDownloadUrl: async (id: string): Promise<AttachmentDownloadResponse> => {
    const response = await apiClient.get<AttachmentDownloadResponse>(
      `${CRM_BASE}/attachments/${id}/download`,
    );
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/attachments/${id}`);
  },
};

export const searchApi = {
  search: async (params: {
    q: string;
    types?: string[];
    limit?: number;
  }): Promise<SearchResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);
    if (params.types?.length) {
      params.types.forEach((t) => searchParams.append('types', t));
    }
    if (params.limit) {
      searchParams.set('limit', String(params.limit));
    }
    const response = await apiClient.get<SearchResponse>(
      `${CRM_BASE}/search?${searchParams.toString()}`,
    );
    return response;
  },
};

// ===== Import API =====
export interface CRMImportJob {
  id: string;
  tenantId: string;
  entityType: string;
  fileName: string;
  fileKey: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
  totalRows: number;
  processedRows: number;
  successCount: number;
  errorCount: number;
  updatedCount: number;
  skippedCount: number;
  duplicateMode: string;
  assignmentMode: string;
  errors: Array<{ row: number; error: string }>;
  createdById: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ImportPresignResponse {
  uploadUrl: string;
  fileKey: string;
  expiresAt: string;
}

export const importApi = {
  presign: async (
    entityType: string,
    dto: { fileName: string; contentType: string; fileSize: number },
  ) => {
    const response = await apiClient.post(`${CRM_BASE}/import/${entityType}/presign`, dto);
    return response as ImportPresignResponse;
  },

  start: async (
    entityType: string,
    dto: {
      fileKey: string;
      fileName: string;
      duplicateMode?: string;
      assignmentMode?: string;
      campaignId?: string;
    },
  ) => {
    const response = await apiClient.post(`${CRM_BASE}/import/${entityType}/start`, dto);
    return response as CRMImportJob;
  },

  getJobs: async () => {
    const response = await apiClient.get(`${CRM_BASE}/import/jobs`);
    return response as CRMImportJob[];
  },

  getJob: async (id: string) => {
    const response = await apiClient.get(`${CRM_BASE}/import/jobs/${id}`);
    return response as CRMImportJob;
  },
};

// ---- Custom Fields ----
export interface CreateCustomFieldInput {
  entityType: string;
  fieldName: string;
  label: string;
  fieldType: string;
  isRequired?: boolean;
  defaultValue?: string;
  options?: string[];
  displayOrder?: number;
  /** Inventory M1 (v1.4 C2): required for entityType 'product', forbidden otherwise. */
  businessLineId?: string;
  /** Inventory M1 (FR-2): numeric range for NUMBER/CURRENCY fields. */
  minValue?: number;
  maxValue?: number;
}

export interface UpdateCustomFieldInput {
  label?: string;
  isRequired?: boolean;
  defaultValue?: string;
  options?: string[];
  displayOrder?: number;
  isActive?: boolean;
  /** Inventory M1 (FR-2): numeric range for NUMBER/CURRENCY fields. */
  minValue?: number;
  maxValue?: number;
}

// Cycle 50.3 — Pre-delete impact report. Returned by GET .../references
// and embedded in the 409 Conflict body when a delete is refused.
export interface CustomFieldReferences {
  field: { id: string; fieldName: string; label: string; entityType: string };
  intakeForms: { id: string; name: string }[];
  recordCount: number;
}

export const customFieldsApi = {
  getAll: async (
    entityType?: string,
    includeInactive?: boolean,
    businessLineId?: string,
  ): Promise<CustomFieldDefinition[]> => {
    const params: Record<string, string> = {};
    if (entityType) params.entityType = entityType;
    if (includeInactive) params.includeInactive = 'true';
    // Inventory M1 (v1.4 FR-32): narrows product definitions to one line.
    if (businessLineId) params.businessLineId = businessLineId;
    const response = await apiClient.get<CustomFieldDefinition[]>(`${CRM_BASE}/custom-fields`, {
      params,
    });
    return response;
  },

  getById: async (id: string): Promise<CustomFieldDefinition> => {
    const response = await apiClient.get<CustomFieldDefinition>(`${CRM_BASE}/custom-fields/${id}`);
    return response;
  },

  create: async (data: CreateCustomFieldInput): Promise<CustomFieldDefinition> => {
    const response = await apiClient.post<CustomFieldDefinition>(`${CRM_BASE}/custom-fields`, data);
    return response;
  },

  update: async (id: string, data: UpdateCustomFieldInput): Promise<CustomFieldDefinition> => {
    const response = await apiClient.put<CustomFieldDefinition>(
      `${CRM_BASE}/custom-fields/${id}`,
      data,
    );
    return response;
  },

  reorder: async (fields: Array<{ id: string; displayOrder: number }>): Promise<void> => {
    await apiClient.put<void>(`${CRM_BASE}/custom-fields/reorder`, { fields });
  },

  // Cycle 50.3 — fetch the impact report before showing the delete dialog
  references: async (id: string): Promise<CustomFieldReferences> => {
    return apiClient.get<CustomFieldReferences>(`${CRM_BASE}/custom-fields/${id}/references`);
  },

  // Cycle 50.3 — `force=true` cascades the field name out of every
  // referencing intake form (server-side). Without it, the server
  // returns 409 with the references list inside details.
  delete: async (id: string, force = false): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/custom-fields/${id}${force ? '?force=true' : ''}`);
  },
};

// ============================================================================
// SITE VISITS API
// ============================================================================
export const siteVisitsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<SiteVisit>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<SiteVisit>>(
      `${CRM_BASE}/site-visits?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<SiteVisit> => {
    const response = await apiClient.get<SiteVisit>(`${CRM_BASE}/site-visits/${id}`);
    return response;
  },

  create: async (input: CreateSiteVisitInput): Promise<SiteVisit> => {
    const response = await apiClient.post<SiteVisit>(`${CRM_BASE}/site-visits`, input);
    return response;
  },

  update: async (id: string, input: UpdateSiteVisitInput): Promise<SiteVisit> => {
    const response = await apiClient.put<SiteVisit>(`${CRM_BASE}/site-visits/${id}`, input);
    return response;
  },

  checkIn: async (id: string, input: CheckInInput): Promise<SiteVisit> => {
    const response = await apiClient.patch<SiteVisit>(
      `${CRM_BASE}/site-visits/${id}/check-in`,
      input,
    );
    return response;
  },

  checkOut: async (id: string, input: CheckOutInput): Promise<SiteVisit> => {
    const response = await apiClient.patch<SiteVisit>(
      `${CRM_BASE}/site-visits/${id}/check-out`,
      input,
    );
    return response;
  },

  complete: async (id: string, input: CompleteSiteVisitInput): Promise<SiteVisit> => {
    const response = await apiClient.patch<SiteVisit>(
      `${CRM_BASE}/site-visits/${id}/complete`,
      input,
    );
    return response;
  },

  cancel: async (id: string): Promise<SiteVisit> => {
    const response = await apiClient.patch<SiteVisit>(`${CRM_BASE}/site-visits/${id}/cancel`);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/site-visits/${id}`);
  },

  myDay: async (): Promise<SiteVisit[]> => {
    const response = await apiClient.get<SiteVisit[]>(`${CRM_BASE}/site-visits/my-day`);
    return response;
  },

  getStats: async (): Promise<SiteVisitStats> => {
    const response = await apiClient.get<SiteVisitStats>(`${CRM_BASE}/site-visits/stats`);
    return response;
  },

  // Photo operations
  presignPhoto: async (
    visitId: string,
    input: PresignPhotoInput,
  ): Promise<PresignedPhotoResponse> => {
    const response = await apiClient.post<PresignedPhotoResponse>(
      `${CRM_BASE}/site-visits/${visitId}/photos/presign`,
      input,
    );
    return response;
  },

  confirmPhoto: async (visitId: string, input: ConfirmPhotoInput): Promise<SiteVisitPhoto> => {
    const response = await apiClient.post<SiteVisitPhoto>(
      `${CRM_BASE}/site-visits/${visitId}/photos/confirm`,
      input,
    );
    return response;
  },

  // R4 (E8): the units shown on a visit
  listUnits: async (visitId: string): Promise<SiteVisitUnit[]> => {
    return apiClient.get<SiteVisitUnit[]>(`${CRM_BASE}/site-visits/${visitId}/units`);
  },

  upsertUnit: async (
    visitId: string,
    productId: string,
    input: UpsertSiteVisitUnitInput,
  ): Promise<SiteVisitUnit[]> => {
    return apiClient.put<SiteVisitUnit[]>(
      `${CRM_BASE}/site-visits/${visitId}/units/${productId}`,
      input,
    );
  },

  removeUnit: async (visitId: string, productId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/site-visits/${visitId}/units/${productId}`);
  },

  listPhotos: async (visitId: string): Promise<SiteVisitPhoto[]> => {
    const response = await apiClient.get<SiteVisitPhoto[]>(
      `${CRM_BASE}/site-visits/${visitId}/photos`,
    );
    return response;
  },

  deletePhoto: async (visitId: string, photoId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/site-visits/${visitId}/photos/${photoId}`);
  },
};

// ============================================================================
// PAYMENTS API
// ============================================================================
export const paymentsApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<Payment>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      `${CRM_BASE}/payments?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<Payment> => {
    const response = await apiClient.get<Payment>(`${CRM_BASE}/payments/${id}`);
    return response;
  },

  create: async (data: Record<string, unknown>): Promise<Payment> => {
    const response = await apiClient.post<Payment>(`${CRM_BASE}/payments`, data);
    return response;
  },

  reverse: async (id: string, data: { reason: string }): Promise<Payment> => {
    const response = await apiClient.post<Payment>(`${CRM_BASE}/payments/${id}/reverse`, data);
    return response;
  },
};

// ============================================================================
// CREDIT NOTES API
// ============================================================================
export const creditNotesApi = {
  getAll: async (params: QueryParams = {}): Promise<PaginatedResponse<CreditNote>> => {
    const query = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<CreditNote>>(
      `${CRM_BASE}/credit-notes?${query}`,
    );
    return response;
  },

  getById: async (id: string): Promise<CreditNote> => {
    const response = await apiClient.get<CreditNote>(`${CRM_BASE}/credit-notes/${id}`);
    return response;
  },

  create: async (data: Record<string, unknown>): Promise<CreditNote> => {
    const response = await apiClient.post<CreditNote>(`${CRM_BASE}/credit-notes`, data);
    return response;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<CreditNote> => {
    const response = await apiClient.patch<CreditNote>(`${CRM_BASE}/credit-notes/${id}`, data);
    return response;
  },

  issue: async (id: string): Promise<CreditNote> => {
    const response = await apiClient.post<CreditNote>(`${CRM_BASE}/credit-notes/${id}/issue`);
    return response;
  },

  apply: async (id: string): Promise<CreditNote> => {
    const response = await apiClient.post<CreditNote>(`${CRM_BASE}/credit-notes/${id}/apply`);
    return response;
  },
};

// ============================================================================
// PAYMENT TERMS API
// ============================================================================
export const paymentTermsApi = {
  getAll: async (): Promise<PaymentTerm[]> => {
    const response = await apiClient.get<PaymentTerm[]>(`${CRM_BASE}/payment-terms`);
    return response;
  },

  create: async (data: Record<string, unknown>): Promise<PaymentTerm> => {
    const response = await apiClient.post<PaymentTerm>(`${CRM_BASE}/payment-terms`, data);
    return response;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<PaymentTerm> => {
    const response = await apiClient.patch<PaymentTerm>(`${CRM_BASE}/payment-terms/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/payment-terms/${id}`);
  },
};

// ============================================================================
// PAYMENT METHODS API
// ============================================================================
export const paymentMethodsApi = {
  getAll: async (): Promise<PaymentMethod[]> => {
    const response = await apiClient.get<PaymentMethod[]>(`${CRM_BASE}/payment-methods`);
    return response;
  },

  create: async (data: Record<string, unknown>): Promise<PaymentMethod> => {
    const response = await apiClient.post<PaymentMethod>(`${CRM_BASE}/payment-methods`, data);
    return response;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<PaymentMethod> => {
    const response = await apiClient.patch<PaymentMethod>(
      `${CRM_BASE}/payment-methods/${id}`,
      data,
    );
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/payment-methods/${id}`);
  },
};

// ============================================================================
// TAX API
// ============================================================================
export const taxApi = {
  // Regimes
  getRegimes: async (): Promise<TaxRegime[]> => {
    const response = await apiClient.get<TaxRegime[]>(`${CRM_BASE}/tax/regimes`);
    return response;
  },

  getRegime: async (id: string): Promise<TaxRegime> => {
    const response = await apiClient.get<TaxRegime>(`${CRM_BASE}/tax/regimes/${id}`);
    return response;
  },

  createRegime: async (data: Record<string, unknown>): Promise<TaxRegime> => {
    const response = await apiClient.post<TaxRegime>(`${CRM_BASE}/tax/regimes`, data);
    return response;
  },

  updateRegime: async (id: string, data: Record<string, unknown>): Promise<TaxRegime> => {
    const response = await apiClient.patch<TaxRegime>(`${CRM_BASE}/tax/regimes/${id}`, data);
    return response;
  },

  deleteRegime: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/tax/regimes/${id}`);
  },

  // Components
  getComponents: async (regimeId: string): Promise<TaxComponent[]> => {
    const response = await apiClient.get<TaxComponent[]>(
      `${CRM_BASE}/tax/regimes/${regimeId}/components`,
    );
    return response;
  },

  createComponent: async (
    regimeId: string,
    data: Record<string, unknown>,
  ): Promise<TaxComponent> => {
    const response = await apiClient.post<TaxComponent>(
      `${CRM_BASE}/tax/regimes/${regimeId}/components`,
      data,
    );
    return response;
  },

  deleteComponent: async (regimeId: string, componentId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/tax/regimes/${regimeId}/components/${componentId}`);
  },

  // Groups
  getGroups: async (): Promise<TaxGroup[]> => {
    const response = await apiClient.get<TaxGroup[]>(`${CRM_BASE}/tax/groups`);
    return response;
  },

  getGroup: async (id: string): Promise<TaxGroup> => {
    const response = await apiClient.get<TaxGroup>(`${CRM_BASE}/tax/groups/${id}`);
    return response;
  },

  createGroup: async (data: Record<string, unknown>): Promise<TaxGroup> => {
    const response = await apiClient.post<TaxGroup>(`${CRM_BASE}/tax/groups`, data);
    return response;
  },

  updateGroup: async (id: string, data: Record<string, unknown>): Promise<TaxGroup> => {
    const response = await apiClient.patch<TaxGroup>(`${CRM_BASE}/tax/groups/${id}`, data);
    return response;
  },

  deleteGroup: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/tax/groups/${id}`);
  },

  addGroupRate: async (groupId: string, data: Record<string, unknown>): Promise<void> => {
    await apiClient.post<void>(`${CRM_BASE}/tax/groups/${groupId}/rates`, data);
  },

  deleteGroupRate: async (groupId: string, rateId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/tax/groups/${groupId}/rates/${rateId}`);
  },
};

// ============================================================================
// PRICE BOOKS API
// ============================================================================
export const priceBooksApi = {
  getAll: async (): Promise<PriceBook[]> => {
    const response = await apiClient.get<PriceBook[]>(`${CRM_BASE}/price-books`);
    return response;
  },

  getById: async (id: string): Promise<PriceBook> => {
    const response = await apiClient.get<PriceBook>(`${CRM_BASE}/price-books/${id}`);
    return response;
  },

  create: async (data: Record<string, unknown>): Promise<PriceBook> => {
    const response = await apiClient.post<PriceBook>(`${CRM_BASE}/price-books`, data);
    return response;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<PriceBook> => {
    const response = await apiClient.patch<PriceBook>(`${CRM_BASE}/price-books/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/price-books/${id}`);
  },

  addEntry: async (priceBookId: string, data: Record<string, unknown>): Promise<PriceBookEntry> => {
    const response = await apiClient.post<PriceBookEntry>(
      `${CRM_BASE}/price-books/${priceBookId}/entries`,
      data,
    );
    return response;
  },

  updateEntry: async (
    priceBookId: string,
    entryId: string,
    data: Record<string, unknown>,
  ): Promise<PriceBookEntry> => {
    const response = await apiClient.patch<PriceBookEntry>(
      `${CRM_BASE}/price-books/${priceBookId}/entries/${entryId}`,
      data,
    );
    return response;
  },

  removeEntry: async (priceBookId: string, entryId: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/price-books/${priceBookId}/entries/${entryId}`);
  },
};

// ============================================================================
// DISCOUNT RULES API
// ============================================================================
export const discountRulesApi = {
  getAll: async (): Promise<DiscountRule[]> => {
    const response = await apiClient.get<DiscountRule[]>(`${CRM_BASE}/discount-rules`);
    return response;
  },

  create: async (data: Record<string, unknown>): Promise<DiscountRule> => {
    const response = await apiClient.post<DiscountRule>(`${CRM_BASE}/discount-rules`, data);
    return response;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<DiscountRule> => {
    const response = await apiClient.patch<DiscountRule>(`${CRM_BASE}/discount-rules/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/discount-rules/${id}`);
  },
};

// ============================================================================
// EXCHANGE RATES API
// ============================================================================
export const exchangeRatesApi = {
  getAll: async (fromCurrency?: string, toCurrency?: string): Promise<ExchangeRate[]> => {
    const params = new URLSearchParams();
    if (fromCurrency) params.append('fromCurrency', fromCurrency);
    if (toCurrency) params.append('toCurrency', toCurrency);
    const query = params.toString();
    const response = await apiClient.get<ExchangeRate[]>(
      `${CRM_BASE}/exchange-rates${query ? `?${query}` : ''}`,
    );
    return response;
  },

  getById: async (id: string): Promise<ExchangeRate> => {
    const response = await apiClient.get<ExchangeRate>(`${CRM_BASE}/exchange-rates/${id}`);
    return response;
  },

  create: async (data: Record<string, unknown>): Promise<ExchangeRate> => {
    const response = await apiClient.post<ExchangeRate>(`${CRM_BASE}/exchange-rates`, data);
    return response;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<ExchangeRate> => {
    const response = await apiClient.patch<ExchangeRate>(`${CRM_BASE}/exchange-rates/${id}`, data);
    return response;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete<void>(`${CRM_BASE}/exchange-rates/${id}`);
  },
};

// ============================================================================
// DOCUMENT FLOW API
// ============================================================================
export const documentFlowApi = {
  getFlow: async (documentType: string, documentId: string): Promise<DocumentFlowItem[]> => {
    const response = await apiClient.get<DocumentFlowItem[]>(
      `${CRM_BASE}/document-flow/${documentType}/${documentId}`,
    );
    return response;
  },
};

// ============================================================================
// ENTITY HISTORY API (generic for financial entities)
// ============================================================================
export const entityHistoryApi = {
  getHistory: async (entityType: string, entityId: string): Promise<EntityHistory[]> => {
    const response = await apiClient.get<EntityHistory[]>(
      `${CRM_BASE}/${entityType}/${entityId}/history`,
    );
    return response;
  },
};

// ============================================================================
// EMPLOYEES API (scope-restricted for reassignment)
// ============================================================================
export interface AssignableEmployeeRaw {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  email: string;
  employeeCode: string;
  department: { id: string; name: string } | null;
  designation: { id: string; name: string } | null;
}

export interface AssignableEmployeesResponse {
  scope: 'self' | 'team' | 'department' | 'tenant';
  employees: AssignableEmployeeRaw[];
}

export type CrmAssignableEntityType =
  | 'lead'
  | 'deal'
  | 'contact'
  | 'account'
  | 'case'
  | 'task'
  | 'contract'
  | 'invoice'
  | 'sales-order'
  | 'territory'
  | 'forecast';

export const employeesApi = {
  getAssignable: async (
    entityType?: CrmAssignableEntityType,
  ): Promise<AssignableEmployeesResponse> => {
    const params = entityType ? `?entityType=${entityType}` : '';
    return apiClient.get<AssignableEmployeesResponse>(`${CRM_BASE}/employees/assignable${params}`);
  },
};

// ============================================================================
// INTEGRATIONS API (Cycle 43)
// ============================================================================
/**
 * Tenant-side ZeptoMail integration status (Phase 2.6.3-trimmed).
 *
 * Webhook auth is now PLATFORM-OWNED (Phase 2.6.2.A — tenantless
 * `/zeptomail-webhook/events` route, SuperAdmin-managed static header).
 * The legacy per-tenant config fields (webhookUrl, configMissing,
 * secretPreview*, authHeaderName, authHeaderValuePreview*) are no
 * longer surfaced to tenant admins; only the per-tenant event telemetry
 * remains here. The backend response keeps those fields for backward
 * compat — they're cleaned up in Phase 2.6.5.
 */
export interface ZeptoIntegrationStatus {
  eventsLast7d: number;
  failedLast7d: number;
  lastEventAt: string | null;
  lastEventOk: boolean | null;
}

export const integrationsApi = {
  getZeptoStatus: async (): Promise<ZeptoIntegrationStatus> => {
    return apiClient.get<ZeptoIntegrationStatus>(`${CRM_BASE}/integrations/zepto/status`);
  },
  // Per-tenant rotation endpoints (regenerateZeptoSecret +
  // regenerateZeptoAuthHeaderValue) were removed in Phase 2.6.3 — rotation
  // moved to the SuperAdmin platform endpoint (`adminService.zeptoPlatform
  // .rotateAuthHeader`). Backend endpoints stay until Phase 2.6.4.
};

// Export all APIs
export const crmService = {
  leads: leadsApi,
  contacts: contactsApi,
  accounts: accountsApi,
  deals: dealsApi,
  tasks: tasksApi,
  activities: activitiesApi,
  notes: notesApi,
  pipelines: pipelinesApi,
  emailTemplates: emailTemplatesApi,
  campaigns: campaignsApi,
  products: productsApi,
  vendors: vendorsApi,
  quotes: quotesApi,
  salesOrders: salesOrdersApi,
  purchaseOrders: purchaseOrdersApi,
  invoices: invoicesApi,
  cases: casesApi,
  workflows: workflowsApi,
  analytics: analyticsApi,
  bulkOperations: bulkOperationsApi,
  duplicates: duplicatesApi,
  leadScoring: leadScoringApi,
  webhooks: webhooksApi,
  accountContacts: accountContactsApi,
  approvals: approvalsApi,
  sla: slaApi,
  contracts: contractsApi,
  forecasts: forecastsApi,
  territories: territoriesApi,
  competitors: competitorsApi,
  settings: settingsApi,
  attachments: attachmentsApi,
  import: importApi,
  search: searchApi,
  customFields: customFieldsApi,
  siteVisits: siteVisitsApi,
  payments: paymentsApi,
  creditNotes: creditNotesApi,
  paymentTerms: paymentTermsApi,
  paymentMethods: paymentMethodsApi,
  tax: taxApi,
  priceBooks: priceBooksApi,
  discountRules: discountRulesApi,
  exchangeRates: exchangeRatesApi,
  documentFlow: documentFlowApi,
  entityHistory: entityHistoryApi,
  employees: employeesApi,
};

export default crmService;
